import { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API, apiClient } from "@/App";
import { useBranding } from "@/context/BrandingContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import NotificationSettings from "@/components/NotificationSettings";
import RideTracking from "@/components/RideTracking";
import PackageDelivery from "@/pages/customer/PackageDelivery";
import CityVoteModal from "@/components/CityVoteModal";
import { useWebSocket } from "@/hooks/useRideTracking";
import LanguageSelector from "@/components/LanguageSelector";
import { playWhistleSound } from "@/services/pushNotifications";
import {
  Home,
  Clock,
  User,
  MapPin,
  Navigation,
  Bike,
  Car,
  Truck,
  Star,
  Phone,
  ChevronRight,
  LogOut,
  IndianRupee,
  Map,
  Menu,
  X,
  ArrowLeft,
  Locate,
  MessageCircle,
  Wifi,
  WifiOff,
  Shield,
  Package,
  RefreshCw,
  Timer,
  Calendar,
  ArrowLeftRight,
  AlertCircle,
  Headphones,
  ExternalLink,
  RotateCcw,
  Bookmark,
  Building2,
  GraduationCap,
  Briefcase,
  Heart,
  Trash2,
  Edit3,
  Plus,
  ChevronDown,
  Loader2,
  Check,
  Pencil,
} from "lucide-react";

// Auto Rickshaw SVG Icon Component - Clean line art style
const AutoRickshawIcon = ({ className = "w-10 h-10" }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Roof/Canopy */}
    <path 
      d="M8 20C8 18 10 16 12 16H44C46 16 48 18 48 20V22H8V20Z" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    {/* Body frame */}
    <path 
      d="M8 22V42C8 44 10 46 12 46H16M48 22V36M48 36C52 36 54 38 54 40V42M48 36V22" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    {/* Window (left side) */}
    <rect x="12" y="24" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
    {/* Door opening */}
    <path d="M30 24V42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M30 24H44V42H30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    {/* Door handle */}
    <ellipse cx="40" cy="34" rx="2" ry="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    {/* Mirror/handle at top */}
    <path d="M44 20C46 18 48 18 48 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Rear wheel (larger) */}
    <circle cx="18" cy="48" r="7" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <circle cx="18" cy="48" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    {/* Front wheel (smaller) */}
    <circle cx="52" cy="48" r="5" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <circle cx="52" cy="48" r="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    {/* Fender/mudguard */}
    <path d="M6 46H10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    {/* Ground shadow line */}
    <path d="M10 56H56" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    {/* Handlebar */}
    <path d="M54 42V44C54 45 55 46 56 46" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import LocationPicker, { calculateDistance } from "@/components/LocationPicker";
import LocationPermissionBanner from "@/components/LocationPermissionBanner";
import { ChatButton } from "@/components/ChatCall";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Import extracted components for refactored booking page
import { ActiveRideCard } from "./components/ActiveRideCard";
import { VehicleSelector } from "./components/VehicleSelector";
import { BookingTypeSelector } from "./components/BookingTypeSelector";
import { BookingOptionsPanel } from "./components/BookingOptionsPanel";
import { CancelRideDialog } from "./components/CancelRideDialog";
import { RatingDialog } from "./components/RatingDialog";

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const { appName, logoBase64 } = useBranding();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("home");
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportContacts, setSupportContacts] = useState([]);
  const [loadingSupport, setLoadingSupport] = useState(false);

  const fetchSupportContacts = async () => {
    if (!user?.id) return;
    setLoadingSupport(true);
    try {
      const res = await axios.get(`${API}/settings/support/contacts/customer/${user.id}`);
      setSupportContacts(res.data.contacts || []);
    } catch (err) {
      // Failed to fetch support contacts - non-critical
    } finally {
      setLoadingSupport(false);
    }
  };

  const openWhatsApp = (contact) => {
    const phone = contact.whatsapp_number;
    const message = encodeURIComponent(contact.message || "I need help in Booking");
    window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
  };

  const handleSupportClick = () => {
    fetchSupportContacts();
    setSupportOpen(true);
  };

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (path === "history") setActiveTab("history");
    else if (path === "profile") setActiveTab("profile");
    else setActiveTab("home");
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success(t('logout') + " successful");
  };

  const navItems = [
    { id: "home", icon: Home, label: t('book_ride'), path: "/customer" },
    { id: "history", icon: Clock, label: t('history'), path: "/customer/history" },
    { id: "profile", icon: User, label: t('profile'), path: "/customer/profile" },
  ];

  // Detect if user is on iOS device
  const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Handle Join as Rider click - navigate to appropriate app store
  const handleJoinAsRider = () => {
    // Get Rider App URLs from fetched data
    const riderPlaystoreUrl = appDownloadUrls?.playstore_url || appDownloadUrls?.rider_playstore_url;
    const riderAppstoreUrl = appDownloadUrls?.appstore_url || appDownloadUrls?.rider_appstore_url;
    
    // Determine which URL to use based on device
    let targetUrl;
    if (isIOSDevice && riderAppstoreUrl) {
      targetUrl = riderAppstoreUrl;
    } else if (riderPlaystoreUrl) {
      targetUrl = riderPlaystoreUrl;
    } else {
      // Fallback to rider web app if no store URLs configured
      targetUrl = '/rider/';
    }
    
    window.open(targetUrl, '_blank');
  };

  // Mobile bottom nav for tile-based navigation (avoids phone notch/controls overlap)
  const MobileBottomNav = () => (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 pb-safe">
      <div className="flex items-center justify-around py-2 px-4">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all min-w-[60px] ${
              activeTab === item.id
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted"
            }`}
            data-testid={`mobile-nav-tile-${item.id}`}
          >
            <item.icon className={`w-5 h-5 ${activeTab === item.id ? "text-primary" : ""}`} />
            <span className="text-xs font-medium">{item.label}</span>
          </button>
        ))}
        <button
          onClick={handleSupportClick}
          className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl text-green-600 hover:bg-green-50 min-w-[60px]"
          data-testid="mobile-nav-tile-support"
        >
          <Headphones className="w-5 h-5" />
          <span className="text-xs font-medium">Support</span>
        </button>
        <button
          onClick={handleJoinAsRider}
          className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl text-orange-600 hover:bg-orange-50 min-w-[60px]"
          data-testid="mobile-nav-tile-join-rider"
        >
          <Bike className="w-5 h-5" />
          <span className="text-xs font-medium">Earn</span>
        </button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header Navigation */}
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between p-4">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => navigate('/customer')}
            data-testid="logo-home-link"
          >
            {logoBase64 ? (
              <img src={logoBase64} alt={appName} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <Bike className="w-5 h-5 text-primary-foreground" />
              </div>
            )}
            <div>
              <h1 className="font-bold text-foreground">{appName}</h1>
              <p className="text-xs text-muted-foreground">{t('hello')}, {user?.name?.split(" ")[0]}</p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.id}
                variant={activeTab === item.id ? "default" : "ghost"}
                size="sm"
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.label}
              </Button>
            ))}
            {/* Support Button - Desktop */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSupportClick}
              className="text-green-600 hover:text-green-700 hover:bg-green-50"
              data-testid="nav-support"
            >
              <Headphones className="w-4 h-4 mr-2" />
              Support
            </Button>
            {/* Join as Rider Button - Desktop */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleJoinAsRider}
              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
              data-testid="nav-join-rider"
            >
              <Bike className="w-4 h-4 mr-2" />
              Join as Rider
            </Button>
            {/* Language Selector - Desktop */}
            <LanguageSelector variant="ghost" />
          </nav>

          {/* Mobile: Language selector and logout only (navigation is now in bottom tiles) */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector variant="ghost" size="icon" />
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              className="text-muted-foreground hover:text-destructive"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Support Dialog */}
      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headphones className="w-5 h-5 text-green-600" />
              Support
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Contact our support team via WhatsApp for quick assistance with your bookings.
            </p>
            
            {loadingSupport ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : supportContacts.length > 0 ? (
              <div className="space-y-3">
                {supportContacts.map((contact, idx) => (
                  <button
                    key={idx}
                    onClick={() => openWhatsApp(contact)}
                    className="w-full flex items-center justify-between p-4 rounded-xl border hover:border-green-300 hover:bg-green-50 transition-all"
                    data-testid={`support-contact-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        {contact.role === 'admin' ? (
                          <Shield className="w-5 h-5 text-green-600" />
                        ) : (
                          <User className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {contact.role === 'admin' ? 'Admin Support' : 'City Support'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Chat</span>
                      <ExternalLink className="w-3 h-3" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground opacity-50 mb-3" />
                <p className="text-muted-foreground">No support contacts available yet.</p>
                <p className="text-sm text-muted-foreground mt-1">Please try again later.</p>
              </div>
            )}
            
            <p className="text-xs text-center text-muted-foreground pt-2 border-t">
              Default message: "I need help in Booking"
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main content with bottom padding for mobile nav */}
      <div className="pb-20 md:pb-0">
        <Routes>
          <Route index element={<BookingPage />} />
          <Route path="package" element={<PackageDelivery />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Routes>
      </div>

      {/* Mobile Bottom Navigation Tiles */}
      <MobileBottomNav />
    </div>
  );
};

const BookingPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [vehicleType, setVehicleType] = useState(null); // No default selection for new bookings
  const [vehicleSubtype, setVehicleSubtype] = useState(null);
  const [allowSubtypeMatching, setAllowSubtypeMatching] = useState(true);
  const [vehicleSettings, setVehicleSettings] = useState([]);
  const [vehicleSettingsLoaded, setVehicleSettingsLoaded] = useState(false);
  const [fareEstimate, setFareEstimate] = useState(null);
  const [showBookingSheet, setShowBookingSheet] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapPickerSelectionMode, setMapPickerSelectionMode] = useState(null); // Track LocationPicker's selection mode
  const [activeRide, setActiveRide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState("");
  const [driverLocation, setDriverLocation] = useState(null);
  const [showTrackingMap, setShowTrackingMap] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showCall, setShowCall] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  
  // External Services state for More Services section
  const [moreServicesConfig, setMoreServicesConfig] = useState({
    section_enabled: true,
    section_title: "More Services",
    services: []
  });
  const [showComingSoon, setShowComingSoon] = useState(false);
  
  // "I will tell driver" mode for drop location
  const [tellDriverMode, setTellDriverMode] = useState(false);
  const [approximateKm, setApproximateKm] = useState(null);
  const [customKm, setCustomKm] = useState("");
  const [incomingCall, setIncomingCall] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [showCustomTip, setShowCustomTip] = useState(false);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const driverLocationIntervalRef = useRef(null);
  const activeRideCardRef = useRef(null); // Ref for scrolling to active ride
  
  // Cancellation states
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [customCancelReason, setCustomCancelReason] = useState("");
  const [reassignToOther, setReassignToOther] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Nearest drivers list state (persists after ride cancellation)
  const [showNearestDrivers, setShowNearestDrivers] = useState(false);
  const [nearestDrivers, setNearestDrivers] = useState([]);
  
  // City Vote Modal state (for areas with no drivers)
  const [showCityVoteModal, setShowCityVoteModal] = useState(false);
  const [voteModalCity, setVoteModalCity] = useState(null);
  
  const CANCELLATION_REASONS = [
    { id: "high_price", label: "Driver Demanding High Price" },
    { id: "driver_delay", label: "Driver Delay" },
    { id: "not_reachable", label: "Driver Not Reachable" },
    { id: "driver_asked", label: "Driver Asked to Cancel the Ride" },
    { id: "other", label: "Other" },
  ];
  
  // New booking type states
  const [bookingType, setBookingType] = useState("one_way");
  const [hours, setHours] = useState(1);
  const [days, setDays] = useState(1);
  const [estimatedWaitMinutes, setEstimatedWaitMinutes] = useState(30);
  const [bookingPricing, setBookingPricing] = useState(null);
  const [packageWeight, setPackageWeight] = useState(1);  // Weight in kg for package delivery
  const [receiverPhone, setReceiverPhone] = useState("");  // Receiver phone for package delivery

  // Saved addresses states
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showSaveAddressDialog, setShowSaveAddressDialog] = useState(false);
  const [addressToSave, setAddressToSave] = useState({ pickup: null, dropoff: null });
  const [savingAddress, setSavingAddress] = useState(false);
  const [showManageAddresses, setShowManageAddresses] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  // Favorite riders states
  const [favoriteRiders, setFavoriteRiders] = useState([]);
  const [selectedFavoriteRider, setSelectedFavoriteRider] = useState(null);
  const [showFavoriteRiders, setShowFavoriteRiders] = useState(false);
  const [addingToFavorites, setAddingToFavorites] = useState(false);
  const [isRiderFavorite, setIsRiderFavorite] = useState(false);

  // Collapsible sections states
  const [savedAddressesExpanded, setSavedAddressesExpanded] = useState(false);
  const [favoriteRidersExpanded, setFavoriteRidersExpanded] = useState(false);

  // App download banner state (for web users only)
  const [appDownloadUrls, setAppDownloadUrls] = useState(null);
  const [showDownloadBanner, setShowDownloadBanner] = useState(false);
  const isWeb = Capacitor.getPlatform() === 'web';

  const ADDRESS_LABELS = [
    { id: "Home", icon: Home, color: "text-blue-500" },
    { id: "Work", icon: Briefcase, color: "text-amber-500" },
    { id: "School", icon: GraduationCap, color: "text-green-500" },
    { id: "College", icon: Building2, color: "text-purple-500" },
    { id: "Gym", icon: Heart, color: "text-red-500" },
    { id: "Other", icon: Bookmark, color: "text-gray-500" },
  ];

  // Initialize WebSocket connection
  const { isConnected: wsConnected, subscribe } = useWebSocket(user?.id);

  // Handle WebSocket messages for ride updates
  useEffect(() => {
    if (!user?.id) return;

    const unsubRideAccepted = subscribe('ride_accepted', async (data) => {
      if (data.ride && data.ride.customer_id === user.id) {
        setActiveRide(data.ride);
        toast.success('Driver found! ' + (data.ride.driver_name || 'Your driver') + ' is on the way.');
        // Play whistle sound for ride acceptance
        try {
          await playWhistleSound();
        } catch (e) {
          // Silent fail
        }
      }
    });

    const unsubRideStatus = subscribe('ride_status_update', async (data) => {
      if (data.ride && data.ride.customer_id === user.id) {
        setActiveRide(data.ride);
        const statusMessages = {
          arrived: 'Your driver has arrived!',
          in_progress: 'Your ride has started!',
          completed: 'Ride completed! Thank you for riding.',
          cancelled: 'Your ride was cancelled.'
        };
        if (statusMessages[data.status]) {
          toast.info(statusMessages[data.status]);
          // Play whistle sound for driver arrival
          if (data.status === 'arrived') {
            try {
              await playWhistleSound();
            } catch (e) {
              // Silent fail
            }
          }
        }
      }
    });

    const unsubDriverLocation = subscribe('driver_location_update', (data) => {
      if (activeRide && data.ride_id === activeRide.id && data.location) {
        setDriverLocation(data.location);
      }
    });

    return () => {
      unsubRideAccepted();
      unsubRideStatus();
      unsubDriverLocation();
    };
  }, [user?.id, subscribe, activeRide?.id]);

  // Fetch external services for More Services section
  const fetchExternalServices = async () => {
    try {
      // Use the new global more-services endpoint
      const res = await axios.get(`${API}/settings/more-services/public`);
      if (res.data) {
        setMoreServicesConfig(res.data);
      }
    } catch (err) {
      // Use defaults - non-critical
      console.log("More services fetch failed, using defaults");
      setMoreServicesConfig({
        section_enabled: true,
        section_title: "More Services",
        services: [
          {
            id: "bizil_food",
            name: "Bizil Food",
            description: "Order food delivery",
            url: "https://qr.localappstore.in",
            icon: "🍽️",
            color_from: "#F97316",
            color_to: "#DC2626",
            enabled: true,
            order: 1
          }
        ]
      });
    }
  };

  // Fetch vehicle settings on mount
  useEffect(() => {
    const fetchVehicleSettings = async () => {
      try {
        // Build URL with optional lat/lng if pickup is set
        let url = `${API}/settings/vehicle-settings`;
        if (pickup?.lat && pickup?.lng) {
          url += `?lat=${pickup.lat}&lng=${pickup.lng}`;
          console.log('[VehicleSettings] Fetching with coordinates:', pickup.lat, pickup.lng);
        } else {
          console.log('[VehicleSettings] Fetching without coordinates (no pickup set)');
        }
        const res = await axios.get(url);
        console.log('[VehicleSettings] Response:', res.data.map(v => ({
          type: v.vehicle_type,
          one_way: v.one_way_enabled,
          package: v.package_enabled,
          city: v._city_name
        })));
        setVehicleSettings(res.data);
        setVehicleSettingsLoaded(true);
        // Set default subtype for bike
        const bikeSettings = res.data.find(v => v.vehicle_type === 'bike');
        if (bikeSettings?.subtypes?.length > 0) {
          const firstActive = bikeSettings.subtypes.find(s => s.is_active);
          setVehicleSubtype(firstActive?.id || bikeSettings.subtypes[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch vehicle settings:", err);
        setVehicleSettingsLoaded(true); // Still mark as loaded to avoid blocking
      }
    };
    fetchVehicleSettings();
  }, [pickup?.lat, pickup?.lng]); // Re-fetch when pickup location changes

  // Fetch saved addresses and external services on mount
  useEffect(() => {
    const fetchSavedAddresses = async () => {
      if (!user?.id) return;
      try {
        const res = await axios.get(`${API}/addresses/${user.id}`);
        setSavedAddresses(res.data.addresses || []);
      } catch (err) {
        console.error("Failed to fetch saved addresses:", err);
      }
    };
    fetchSavedAddresses();
    fetchExternalServices();
  }, [user?.id]);

  // Fetch app download URLs for web users
  useEffect(() => {
    const fetchAppUrls = async () => {
      if (!isWeb) return; // Only for web users
      try {
        const res = await axios.get(`${API}/settings/app-urls`);
        // Store URLs if any are available (customer or rider app URLs)
        if (res.data && (res.data.customer_playstore_url || res.data.customer_appstore_url || 
            res.data.playstore_url || res.data.appstore_url)) {
          setAppDownloadUrls(res.data);
          // Check if user has dismissed banner before - only show for customer app URLs
          const dismissed = localStorage.getItem("appBannerDismissed");
          if (!dismissed && (res.data.customer_playstore_url || res.data.customer_appstore_url)) {
            setShowDownloadBanner(true);
          }
        }
      } catch (err) {
        console.error("Failed to fetch app URLs:", err);
      }
    };
    fetchAppUrls();
  }, [isWeb]);

  // Check for rebook data from history page
  useEffect(() => {
    const rebookData = localStorage.getItem("rebookData");
    if (rebookData) {
      try {
        const data = JSON.parse(rebookData);
        if (data.pickup) {
          setPickup({
            lat: data.pickup.lat,
            lng: data.pickup.lng,
            address: data.pickup.address,
            shortAddress: data.pickup.short_address || data.pickup.address?.split(",")[0],
          });
        }
        if (data.dropoff) {
          setDrop({
            lat: data.dropoff.lat,
            lng: data.dropoff.lng,
            address: data.dropoff.address,
            shortAddress: data.dropoff.short_address || data.dropoff.address?.split(",")[0],
          });
        }
        if (data.vehicleType) {
          setVehicleType(data.vehicleType);
        }
        if (data.vehicleSubtype) {
          setVehicleSubtype(data.vehicleSubtype);
        }
        if (data.bookingType) {
          setBookingType(data.bookingType);
        }
        if (data.bookingDetails) {
          setBookingDetails(data.bookingDetails);
        }
        // Clear the rebook data after using it
        localStorage.removeItem("rebookData");
      } catch (err) {
        console.error("Failed to parse rebook data:", err);
        localStorage.removeItem("rebookData");
      }
    }
  }, []);

  // Fetch favorite riders
  useEffect(() => {
    const fetchFavoriteRiders = async () => {
      if (!user?.id) return;
      try {
        const res = await axios.get(`${API}/favorites/${user.id}`);
        setFavoriteRiders(res.data.favorites || []);
      } catch (err) {
        console.error("Failed to fetch favorite riders:", err);
      }
    };
    fetchFavoriteRiders();
  }, [user?.id]);

  // Check if current ride's driver is a favorite (for rating screen)
  useEffect(() => {
    if (activeRide?.driver_id && user?.id) {
      const checkFavorite = async () => {
        try {
          const res = await axios.get(`${API}/favorites/${user.id}/check/${activeRide.driver_id}`);
          setIsRiderFavorite(res.data.is_favorite);
        } catch (err) {
          console.error("Failed to check favorite:", err);
        }
      };
      checkFavorite();
    }
  }, [activeRide?.driver_id, user?.id]);

  // Fetch booking pricing when vehicle type changes
  useEffect(() => {
    const fetchBookingPricing = async () => {
      try {
        const res = await axios.get(`${API}/vehicles/${vehicleType}/booking-pricing`);
        setBookingPricing(res.data);
      } catch (err) {
        console.error("Failed to fetch booking pricing:", err);
      }
    };
    if (vehicleType) {
      fetchBookingPricing();
    }
  }, [vehicleType]);

  // Fetch available vehicles based on pickup location
  useEffect(() => {
    const fetchAvailableVehicles = async () => {
      // Only fetch if we have pickup AND vehicleSettings have been loaded
      if (!pickup || !vehicleSettingsLoaded || vehicleSettings.length === 0) return;
      try {
        const res = await axios.post(`${API}/vehicles/available`, {
          lat: pickup.lat,
          lng: pickup.lng,
          radius_km: 15
        });
        // Update vehicle settings with availability info
        const updatedSettings = vehicleSettings.map(vs => {
          const availability = res.data.find(a => a.vehicle_type === vs.vehicle_type);
          return {
            ...vs,
            is_available: availability?.is_available || false,
            drivers_available: availability?.drivers_available || 0,
            nearest_driver_km: availability?.nearest_driver_km
          };
        });
        setVehicleSettings(updatedSettings);
        
        // Check if current selection is unavailable and NOTIFY user (don't auto-switch)
        const currentVehicleAvailability = updatedSettings.find(v => v.vehicle_type === vehicleType);
        if (currentVehicleAvailability && currentVehicleAvailability.is_available === false) {
          const vehicleName = currentVehicleAvailability.display_name || vehicleType;
          toast.warning(
            `No ${vehicleName} drivers available nearby. Please select a different vehicle type.`,
            { duration: 5000 }
          );
        }
        
        // Check if ALL vehicles are unavailable - show vote modal
        const allUnavailable = updatedSettings.every(v => v.is_available === false);
        if (allUnavailable && updatedSettings.length > 0 && pickup) {
          // Delay showing the modal to let the toast appear first
          setTimeout(() => {
            // Extract city from pickup address (usually last part before country)
            const addressParts = (pickup.address || pickup.shortAddress || "").split(",");
            const cityFromAddress = addressParts.length > 1 ? addressParts[addressParts.length - 2]?.trim() : addressParts[0]?.trim();
            setVoteModalCity(cityFromAddress || "your area");
            setShowCityVoteModal(true);
          }, 1000);
        }
      } catch (err) {
        console.error("Failed to fetch available vehicles:", err);
      }
    };
    fetchAvailableVehicles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, vehicleSettingsLoaded]);

  // Show warning if dropoff distance exceeds selected vehicle's max distance (but don't clear selection)
  useEffect(() => {
    if (pickup && drop && vehicleType && vehicleSettings.length > 0) {
      const tripDistance = calculateDistance(pickup.lat, pickup.lng, drop.lat, drop.lng);
      const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
      const maxDropoff = currentVehicle?.max_dropoff_distance || currentVehicle?.max_ride_distance_km;
      if (maxDropoff && tripDistance > maxDropoff) {
        // Clear selection since this vehicle can't service the trip
        setVehicleType(null);
        setVehicleSubtype(null);
      }
    }
  }, [drop, pickup, vehicleType, vehicleSettings]);

  // Handle incoming call
  const handleIncomingCall = useCallback((call) => {
    setIncomingCall(call);
    setShowCall(true);
  }, []);

  // Check for active ride
  useEffect(() => {
    const checkActiveRide = async () => {
      try {
        const res = await axios.get(`${API}/rides/customer/${user.id}`);
        // First check for active (in-progress) rides
        const active = res.data.find((r) =>
          ["pending", "accepted", "arrived", "in_progress"].includes(r.status)
        );
        if (active) {
          setActiveRide(active);
        } else {
          // If no active ride, check for recently completed rides that need rating
          const completedUnrated = res.data.find((r) =>
            r.status === "completed" && !r.rating && !r.customer_rating
          );
          if (completedUnrated) {
            setActiveRide(completedUnrated);
          } else {
            setActiveRide(null);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (user) checkActiveRide();
    const interval = setInterval(checkActiveRide, 5000);
    return () => clearInterval(interval);
  }, [user]);

  // Scroll to top to show active ride card when it appears
  useEffect(() => {
    if (activeRide && activeRide.status !== "completed") {
      // Scroll to top of page to show the active ride section
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [activeRide?.id]); // Only trigger when a new ride is booked (id changes)

  // Fetch driver location for active rides with assigned driver
  useEffect(() => {
    const fetchDriverLocation = async () => {
      if (activeRide?.id && activeRide?.driver_id && 
          ["accepted", "arrived", "in_progress"].includes(activeRide.status)) {
        try {
          const res = await axios.get(`${API}/rides/${activeRide.id}/driver-location`);
          if (res.data.driver_location) {
            setDriverLocation(res.data.driver_location);
          }
        } catch (err) {
          console.error("Failed to fetch driver location:", err);
        }
      }
    };

    if (activeRide?.driver_id && ["accepted", "arrived", "in_progress"].includes(activeRide.status)) {
      fetchDriverLocation();
      // Poll driver location every 5 seconds
      driverLocationIntervalRef.current = setInterval(fetchDriverLocation, 5000);
    }

    return () => {
      if (driverLocationIntervalRef.current) {
        clearInterval(driverLocationIntervalRef.current);
      }
    };
  }, [activeRide?.id, activeRide?.driver_id, activeRide?.status]);

  const estimateFare = async () => {
    // For hourly/daily bookings, drop-off is optional
    // Also optional when "tell driver" mode is enabled
    const isDropOffOptional = bookingType === "hourly" || bookingType === "daily" || tellDriverMode;
    
    if (!pickup) {
      toast.error("Please select pickup location");
      return;
    }
    
    if (!isDropOffOptional && !drop) {
      toast.error("Please select drop location");
      return;
    }
    
    // Validate tell driver mode has approximate km
    if (tellDriverMode) {
      const estimatedKm = (approximateKm === "other" ? 0 : approximateKm) || parseFloat(customKm) || 0;
      if (estimatedKm <= 0) {
        toast.error("Please select approximate distance");
        return;
      }
    }
    
    // Check if selected vehicle type has available drivers
    const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
    if (currentVehicle && currentVehicle.is_available === false) {
      toast.error(`No ${currentVehicle.display_name || vehicleType} drivers available nearby. Please select a different vehicle type.`);
      return;
    }
    
    // Validate receiver phone for package delivery
    if (bookingType === "package") {
      if (!receiverPhone || receiverPhone.length < 10) {
        toast.error("Please enter receiver's phone number (10 digits)");
        return;
      }
    }

    try {
      const requestData = {
        pickup: {
          lat: pickup.lat,
          lng: pickup.lng,
          address: pickup.shortAddress || pickup.address
        },
        vehicle_type: vehicleType,
        booking_type: bookingType,
      };

      // Add vehicle subtype if selected
      if (vehicleSubtype) {
        const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
        const selectedSubtype = currentVehicle?.subtypes?.find(s => s.id === vehicleSubtype);
        if (selectedSubtype) {
          requestData.vehicle_subtype = selectedSubtype.name;
          requestData.allow_subtype_matching = allowSubtypeMatching;
        }
      }

      // Only include dropoff if provided (not in tell driver mode)
      if (drop && !tellDriverMode) {
        requestData.dropoff = {
          lat: drop.lat,
          lng: drop.lng,
          address: drop.shortAddress || drop.address
        };
      }
      
      // Handle "I will tell driver" mode with approximate distance
      if (tellDriverMode) {
        const estimatedKm = (approximateKm === "other" ? 0 : approximateKm) || parseFloat(customKm) || 0;
        if (estimatedKm <= 0) {
          toast.error("Please select approximate distance");
          return;
        }
        requestData.approximate_distance_km = estimatedKm;
        requestData.tell_driver_mode = true;
      }

      // Add booking type specific parameters
      if (bookingType === "hourly") {
        requestData.hours = hours;
      } else if (bookingType === "daily") {
        requestData.days = days;
      } else if (bookingType === "two_way") {
        requestData.estimated_wait_minutes = estimatedWaitMinutes;
      } else if (bookingType === "package") {
        requestData.package_weight_kg = packageWeight;
        requestData.receiver_phone = receiverPhone;
      }

      const res = await axios.post(`${API}/fare/estimate`, requestData);
      setFareEstimate(res.data);
      setShowBookingSheet(true);
      setPromoCode("");
      setPromoApplied(null);
      setTipAmount(0);
      setCustomTip("");
      setShowCustomTip(false);
    } catch (err) {
      console.error("Fare estimation error:", err);
      toast.error(err.response?.data?.detail || "Failed to estimate fare");
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    setApplyingPromo(true);
    try {
      const res = await axios.post(`${API}/promo-codes/apply`, {
        code: promoCode.toUpperCase(),
        ride_amount: fareEstimate.fare,
        user_id: user.id
      });
      
      if (res.data.valid) {
        setPromoApplied(res.data);
        toast.success(`Promo applied! You save ₹${res.data.discount_amount}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid promo code");
      setPromoApplied(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const bookRide = async () => {
    setLoading(true);
    try {
      const finalTip = showCustomTip ? parseFloat(customTip) || 0 : tipAmount;
      const finalFare = promoApplied ? promoApplied.final_amount : fareEstimate?.fare;
      
      if (!finalFare) {
        toast.error("Please estimate fare first");
        setLoading(false);
        return;
      }
      
      const isDropOffOptional = bookingType === "hourly" || bookingType === "daily" || tellDriverMode;
      
      const rideData = {
        customer_id: user.id,
        pickup: { lat: pickup.lat, lng: pickup.lng, address: pickup.shortAddress || pickup.address },
        vehicle_type: vehicleType,
        fare: finalFare,
        tip_amount: finalTip,
        distance_km: fareEstimate?.distance_km,
        duration_min: fareEstimate?.estimated_time_mins,
        promo_code: promoApplied ? promoCode.toUpperCase() : null,
        surge_multiplier: fareEstimate?.surge_multiplier || 1.0,
        booking_type: bookingType,
        tell_driver_mode: tellDriverMode,
      };

      // Add approximate distance for tell_driver_mode
      if (tellDriverMode) {
        const estimatedKm = (approximateKm === "other" ? 0 : approximateKm) || parseFloat(customKm) || 0;
        rideData.approximate_distance_km = estimatedKm;
      }

      // Add vehicle subtype if selected
      if (vehicleSubtype) {
        const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
        const selectedSubtype = currentVehicle?.subtypes?.find(s => s.id === vehicleSubtype);
        if (selectedSubtype) {
          rideData.vehicle_subtype = selectedSubtype.name;
          rideData.allow_subtype_matching = allowSubtypeMatching;
        }
      }

      // Only include dropoff if provided (optional for hourly/daily/tell_driver_mode)
      if (drop && !tellDriverMode) {
        rideData.dropoff = { lat: drop.lat, lng: drop.lng, address: drop.shortAddress || drop.address };
      }

      // Add booking type specific data
      if (bookingType === "hourly") {
        rideData.hours = hours;
      } else if (bookingType === "daily") {
        rideData.days = days;
      } else if (bookingType === "two_way") {
        rideData.estimated_wait_minutes = estimatedWaitMinutes;
      } else if (bookingType === "package") {
        rideData.package_weight_kg = packageWeight;
        rideData.receiver_phone = receiverPhone;
      }

      // Add preferred driver if selected
      if (selectedFavoriteRider) {
        rideData.preferred_driver_id = selectedFavoriteRider.rider_id;
      }
      
      const res = await axios.post(`${API}/rides`, rideData);
      setActiveRide(res.data);
      setShowBookingSheet(false);
      setPromoCode("");
      setPromoApplied(null);
      setTipAmount(0);
      setCustomTip("");
      setShowCustomTip(false);
      // Scroll will be handled by the useEffect watching activeRide.id
      toast.success(selectedFavoriteRider 
        ? `Ride booked! Requesting ${selectedFavoriteRider.nickname || selectedFavoriteRider.rider?.name || 'your favorite rider'}...`
        : "Ride booked! Finding drivers...");
      
      // Clear favorite rider selection after booking
      setSelectedFavoriteRider(null);
      
      // Check if we should prompt to save addresses
      const pickupAlreadySaved = savedAddresses.some(a => 
        Math.abs(a.lat - pickup.lat) < 0.0005 && Math.abs(a.lng - pickup.lng) < 0.0005
      );
      const dropAlreadySaved = drop ? savedAddresses.some(a => 
        Math.abs(a.lat - drop.lat) < 0.0005 && Math.abs(a.lng - drop.lng) < 0.0005
      ) : true;
      
      // Only prompt if there are addresses that aren't saved yet and we haven't maxed out
      if ((!pickupAlreadySaved || !dropAlreadySaved) && savedAddresses.length < 10) {
        setAddressToSave({
          pickup: !pickupAlreadySaved ? pickup : null,
          dropoff: !dropAlreadySaved && drop ? drop : null,
        });
        // Small delay to let user see the booking success first
        setTimeout(() => {
          if (!pickupAlreadySaved || (!dropAlreadySaved && drop)) {
            setShowSaveAddressDialog(true);
          }
        }, 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to book ride");
    } finally {
      setLoading(false);
    }
  };

  const cancelRide = async (shouldRebook = false) => {
    const reason = cancelReason === "other" ? customCancelReason : 
      CANCELLATION_REASONS.find(r => r.id === cancelReason)?.label || cancelReason;
    
    if (!reason) {
      toast.error("Please select a cancellation reason");
      return;
    }
    
    setCancelling(true);
    const cancelledRideId = activeRide.id;
    const doRebook = shouldRebook || reassignToOther;
    
    try {
      await axios.post(`${API}/rides/${activeRide.id}/cancel`, {
        reason,
        cancelled_by: "customer",
        reassign_to_other: doRebook,
        excluded_driver_id: doRebook ? activeRide.driver_id : null
      });
      
      if (doRebook) {
        toast.success("Ride cancelled. Looking for another driver...");
        // Re-book with excluded driver
        try {
          // Properly construct dropoff from existing ride data
          const dropoffLocation = activeRide.dropoff || activeRide.drop;
          
          if (!dropoffLocation) {
            toast.error("Cannot rebook - missing drop location");
            setActiveRide(null);
            setShowCancelDialog(false);
            setCancelReason("");
            setCustomCancelReason("");
            setReassignToOther(false);
            return;
          }
          
          // Build excluded drivers list - include current driver if assigned
          const excludedDrivers = [];
          if (activeRide.driver_id) {
            excludedDrivers.push(activeRide.driver_id);
          }
          // Also include any previously excluded drivers
          if (activeRide.excluded_driver_ids?.length) {
            excludedDrivers.push(...activeRide.excluded_driver_ids);
          }
          
          const rebookRes = await axios.post(`${API}/rides`, {
            customer_id: user.id,
            pickup: {
              lat: activeRide.pickup?.lat,
              lng: activeRide.pickup?.lng,
              address: activeRide.pickup?.address
            },
            dropoff: {
              lat: dropoffLocation.lat,
              lng: dropoffLocation.lng,
              address: dropoffLocation.address
            },
            vehicle_type: activeRide.vehicle_type,
            vehicle_subtype: activeRide.vehicle_subtype || activeRide.vehicle_subtype_id,
            allow_subtype_matching: activeRide.allow_subtype_matching !== false,
            booking_type: activeRide.booking_type || "one_way",
            fare: activeRide.fare || activeRide.estimated_fare || 50,
            tip_amount: activeRide.tip_amount || 0, // Carry tip to rebooked ride
            excluded_driver_ids: [...new Set(excludedDrivers)] // Remove duplicates
          });
          
          // Update the cancelled ride with rebooked_ride_id
          await axios.put(`${API}/rides/${cancelledRideId}/link-rebook`, {
            rebooked_ride_id: rebookRes.data.id
          }).catch(() => {}); // Non-critical, ignore errors
          
          setActiveRide(rebookRes.data);
          toast.info("New ride request sent to other drivers");
        } catch (rebookErr) {
          console.error("Rebook failed:", rebookErr);
          setActiveRide(null);
          toast.error(rebookErr.response?.data?.detail || "Failed to find another driver. Please try booking again.");
        }
      } else {
        setActiveRide(null);
        toast.success("Ride cancelled");
      }
      
      // Reset cancellation state
      setShowCancelDialog(false);
      setCancelReason("");
      setCustomCancelReason("");
      setReassignToOther(false);
    } catch (err) {
      toast.error("Failed to cancel ride");
    } finally {
      setCancelling(false);
    }
  };

  const submitRating = async () => {
    if (ratingSubmitting) return;
    setRatingSubmitting(true);
    try {
      await axios.post(`${API}/rides/${activeRide.id}/rate`, { rating, review });
      toast.success("Thanks for your feedback!");
      setShowRating(false);
      setActiveRide(null);
      setRating(5);
      setReview("");
    } catch (err) {
      console.error("Rating submission error:", err);
      toast.error(err.response?.data?.detail || "Failed to submit rating");
    } finally {
      setRatingSubmitting(false);
    }
  };

  // Save address functions
  const saveAddress = async (addressData, label, customLabel = null) => {
    setSavingAddress(true);
    try {
      const res = await axios.post(`${API}/addresses/${user.id}`, {
        label,
        custom_label: customLabel,
        address: addressData.address,
        short_address: addressData.shortAddress || addressData.address?.split(",")[0],
        lat: addressData.lat,
        lng: addressData.lng,
      });
      setSavedAddresses(prev => [res.data, ...prev]);
      toast.success(`Address saved as "${customLabel || label}"`);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save address");
      return false;
    } finally {
      setSavingAddress(false);
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      await axios.delete(`${API}/addresses/${user.id}/${addressId}`);
      setSavedAddresses(prev => prev.filter(a => a.id !== addressId));
      toast.success("Address deleted");
    } catch (err) {
      toast.error("Failed to delete address");
    }
  };

  const updateAddress = async (addressId, updates) => {
    try {
      const res = await axios.put(`${API}/addresses/${user.id}/${addressId}`, updates);
      setSavedAddresses(prev => prev.map(a => a.id === addressId ? res.data : a));
      toast.success("Address updated");
      setEditingAddress(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update address");
    }
  };

  const selectSavedAddress = (address, isPickup = true) => {
    const locationData = {
      lat: address.lat,
      lng: address.lng,
      address: address.address,
      shortAddress: address.short_address || address.display_label,
    };
    if (isPickup) {
      setPickup(locationData);
    } else {
      setDrop(locationData);
    }
    toast.success(`${isPickup ? "Pickup" : "Drop-off"} set to ${address.display_label}`);
  };

  // Use current location for pickup
  const [gettingCurrentLocation, setGettingCurrentLocation] = useState(false);
  
  // Check location permission on component mount
  useEffect(() => {
    const checkLocationPermission = async () => {
      const isNative = Capacitor.isNativePlatform();
      
      if (isNative) {
        // Use Capacitor's permission check for native apps
        try {
          const permStatus = await Geolocation.checkPermissions();
          console.log('Native permission status:', permStatus.location);
          if (permStatus.location === 'denied') {
            setLocationPermissionDenied(true);
          }
        } catch (e) {
          console.log('Error checking native permissions:', e);
        }
      } else {
        // Use web Permissions API for PWA/browser
        if (navigator.permissions) {
          try {
            const result = await navigator.permissions.query({ name: 'geolocation' });
            if (result.state === 'denied') {
              setLocationPermissionDenied(true);
            } else if (result.state === 'prompt') {
              setLocationPermissionDenied(false);
            }
            
            // Listen for permission changes
            result.onchange = () => {
              if (result.state === 'granted') {
                setLocationPermissionDenied(false);
              } else if (result.state === 'denied') {
                setLocationPermissionDenied(true);
              }
            };
          } catch (e) {
            console.log('Permissions API not supported for geolocation');
          }
        }
      }
    };
    
    checkLocationPermission();
  }, []);
  
  const handleGetCurrentLocation = async () => {
    setGettingCurrentLocation(true);
    setLocationPermissionDenied(false); // Reset before trying
    
    const isNative = Capacitor.isNativePlatform();
    console.log('Requesting location, native platform:', isNative);
    
    try {
      let latitude, longitude;
      
      if (isNative) {
        // Use Capacitor's native Geolocation for better reliability on native apps
        console.log('Using Capacitor Geolocation...');
        
        // First check/request permission
        const permStatus = await Geolocation.checkPermissions();
        console.log('Permission status:', permStatus.location);
        
        if (permStatus.location === 'denied') {
          // Try to request permission
          const requestResult = await Geolocation.requestPermissions();
          console.log('Permission request result:', requestResult.location);
          
          if (requestResult.location === 'denied') {
            setGettingCurrentLocation(false);
            setLocationPermissionDenied(true);
            toast.error("Location access denied. Please enable it in your device settings.");
            return;
          }
        }
        
        // Get current position
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000
        });
        
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
        console.log('Native location:', latitude, longitude);
        
      } else {
        // Use web browser's geolocation for PWA
        console.log('Using browser geolocation...');
        
        if (!navigator.geolocation) {
          toast.error("Geolocation is not supported by your browser");
          setGettingCurrentLocation(false);
          return;
        }
        
        // Wrap in promise for cleaner async/await
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          });
        });
        
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
        console.log('Browser location:', latitude, longitude);
      }
      
      // Reverse geocode to get address
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
        );
        const data = await response.json();
        
        const locationData = {
          lat: latitude,
          lng: longitude,
          address: data.display_name || "Current Location",
          shortAddress: data.address?.road || data.address?.suburb || data.address?.city || "Current Location"
        };
        
        setPickup(locationData);
        toast.success("Pickup set to your current location");
      } catch (error) {
        // If reverse geocoding fails, still set location with generic name
        setPickup({
          lat: latitude,
          lng: longitude,
          address: "Current Location",
          shortAddress: "Current Location"
        });
        toast.success("Pickup set to your current location");
      }
      
      setGettingCurrentLocation(false);
      
    } catch (error) {
      console.log('Location error:', error);
      setGettingCurrentLocation(false);
      
      // Handle different error types
      if (error.code === 1 || error.message?.includes('denied') || error.message?.includes('permission')) {
        setLocationPermissionDenied(true);
        toast.error("Location access denied. Please enable it in settings.");
      } else if (error.code === 2 || error.message?.includes('unavailable')) {
        toast.error("Location information unavailable. Please try again.");
      } else if (error.code === 3 || error.message?.includes('timeout')) {
        toast.error("Location request timed out. Please try again.");
      } else {
        toast.error("Failed to get your location. Please try again.");
      }
    }
  };

  // Favorite riders functions
  const addToFavorites = async (riderId, nickname = null) => {
    setAddingToFavorites(true);
    try {
      const res = await axios.post(`${API}/favorites/${user.id}`, {
        rider_id: riderId,
        nickname: nickname
      });
      setFavoriteRiders(prev => [res.data, ...prev]);
      setIsRiderFavorite(true);
      toast.success("Rider added to favorites!");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to favorites");
      return false;
    } finally {
      setAddingToFavorites(false);
    }
  };

  const removeFromFavorites = async (favoriteId, riderId) => {
    try {
      await axios.delete(`${API}/favorites/${user.id}/${favoriteId}`);
      setFavoriteRiders(prev => prev.filter(f => f.id !== favoriteId));
      if (activeRide?.driver_id === riderId) {
        setIsRiderFavorite(false);
      }
      if (selectedFavoriteRider?.rider_id === riderId) {
        setSelectedFavoriteRider(null);
      }
      toast.success("Rider removed from favorites");
    } catch (err) {
      toast.error("Failed to remove from favorites");
    }
  };

  const selectFavoriteRider = (favorite) => {
    if (selectedFavoriteRider?.id === favorite.id) {
      setSelectedFavoriteRider(null);
      toast.info("Preference cleared - any available driver");
    } else {
      setSelectedFavoriteRider(favorite);
      toast.success(`Preference set: ${favorite.nickname || favorite.rider?.name || "Favorite rider"}`);
    }
  };

  // Show rating dialog when ride is completed
  useEffect(() => {
    if (activeRide?.status === "completed" && !activeRide.rating) {
      setShowRating(true);
    }
  }, [activeRide]);

  const statusMessages = {
    pending: "Finding nearby drivers...",
    accepted: "Driver is on the way!",
    arrived: "Driver has arrived",
    in_progress: "Enjoy your ride!",
  };

  const distance = pickup && drop ? calculateDistance(pickup.lat, pickup.lng, drop.lat, drop.lng) : null;

  // Full screen map picker
  if (showMapPicker) {
    return (
      <div className="fixed inset-0 z-[60] bg-background flex flex-col">
        <header className="bg-card p-4 border-b border-border flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setShowMapPicker(false)}
            data-testid="back-from-map"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-foreground">Select Locations</h1>
            <p className="text-sm text-muted-foreground">Search or drag markers on map</p>
          </div>
        </header>
        
        <div className="flex-1 relative">
          <LocationPicker
            pickup={pickup}
            drop={drop}
            onPickupChange={setPickup}
            onDropChange={setDrop}
            onConfirm={() => setShowMapPicker(false)}
            onSelectionModeChange={setMapPickerSelectionMode}
          />
          
          {/* I Will Tell Driver Panel - Overlay on map page - Hide when in selection mode */}
          {!mapPickerSelectionMode && (
          <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border p-4 shadow-lg z-[1000]">
            <div className="max-w-md mx-auto space-y-3">
              {/* Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">I'll tell driver my destination</span>
                </div>
                <button
                  onClick={() => {
                    const newTellDriverMode = !tellDriverMode;
                    setTellDriverMode(newTellDriverMode);
                    if (newTellDriverMode) {
                      // Turning ON tell driver mode - clear approximate values
                      setApproximateKm(null);
                      setCustomKm("");
                    }
                    // Don't clear drop when toggling - let user keep their selection
                  }}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    tellDriverMode ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    tellDriverMode ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {/* KM Options when tell driver mode is on */}
              {tellDriverMode && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{t('select_approximate_distance_short')}</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[2, 4, 6].map((km) => (
                      <button
                        key={km}
                        onClick={() => {
                          setApproximateKm(km);
                          setCustomKm("");
                        }}
                        className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                          approximateKm === km 
                            ? 'bg-blue-500 text-white border-blue-500' 
                            : 'bg-muted/50 text-foreground border-border hover:border-blue-300'
                        }`}
                      >
                        {km} km
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        setApproximateKm("other");
                        setCustomKm("");
                      }}
                      className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                        approximateKm === "other"
                          ? 'bg-blue-500 text-white border-blue-500' 
                          : 'bg-muted/50 text-foreground border-border hover:border-blue-300'
                      }`}
                    >
                      Other
                    </button>
                  </div>
                  {approximateKm === "other" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="Enter distance"
                        value={customKm}
                        onChange={(e) => setCustomKm(e.target.value)}
                        autoFocus
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-muted-foreground">km</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Confirm Button */}
              <Button
                className="w-full"
                onClick={() => setShowMapPicker(false)}
                disabled={!pickup || (!tellDriverMode && !drop) || (tellDriverMode && !((approximateKm && approximateKm !== "other") || (customKm && parseFloat(customKm) > 0)))}
              >
                {!pickup 
                  ? 'Select Pickup Location'
                  : tellDriverMode 
                    ? ((approximateKm && approximateKm !== "other") || customKm) 
                      ? 'Confirm & Get Fare'
                      : 'Select Distance'
                    : drop 
                      ? 'Confirm Locations' 
                      : 'Select Drop Location'
                }
              </Button>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="customer-booking-page">
      {/* Driver Tracking Map Modal - Using new RideTracking component */}
      {showTrackingMap && activeRide && (
        <div className="fixed inset-0 z-[60] bg-background flex flex-col">
          <header className="bg-card p-4 border-b border-border flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setShowTrackingMap(false)}
              data-testid="close-tracking-map"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-foreground">Track Driver</h1>
              <p className="text-sm text-muted-foreground">
                {activeRide.driver_name} • {activeRide.status.replace("_", " ")}
              </p>
            </div>
            {wsConnected ? (
              <div className="flex items-center gap-1 text-green-500">
                <Wifi className="w-4 h-4" />
                <span className="text-xs">Live</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-yellow-500">
                <WifiOff className="w-4 h-4" />
                <span className="text-xs">Polling</span>
              </div>
            )}
          </header>
          
          <div className="flex-1 p-4">
            <RideTracking
              rideId={activeRide.id}
              userId={user?.id}
              pickup={activeRide.pickup}
              dropoff={activeRide.drop || activeRide.dropoff}
              onCallDriver={(phone) => window.open(`tel:${phone}`, '_self')}
              onChatDriver={() => {
                setShowTrackingMap(false);
                setShowChat(true);
              }}
              height="calc(100vh - 180px)"
            />
          </div>
        </div>
      )}

      {/* Active Ride Card - Using Extracted Component */}
      <ActiveRideCard
        activeRide={activeRide}
        cardRef={activeRideCardRef}
        driverLocation={driverLocation}
        onShowChat={() => setShowChat(true)}
        onShowTrackingMap={() => setShowTrackingMap(true)}
        onCancelRide={() => setShowCancelDialog(true)}
        onIncomingCall={handleIncomingCall}
        showNearestDrivers={showNearestDrivers}
        setShowNearestDrivers={setShowNearestDrivers}
        nearestDrivers={nearestDrivers}
        setNearestDrivers={setNearestDrivers}
      />

      {/* Booking Form - Show when no active ride */}
      {!activeRide && (
        <div className="pb-40 md:pb-24 bg-gray-50 dark:bg-gray-950 min-h-screen"> {/* Extra padding on mobile for sticky footer + bottom nav */}
          {/* Download App Banner - Web Only */}
          {showDownloadBanner && appDownloadUrls && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bike className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm">Get the BizilGO App</p>
                  <p className="text-xs text-white/80 truncate">Faster booking, real-time tracking</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {appDownloadUrls.customer_playstore_url && (
                  <a
                    href={appDownloadUrls.customer_playstore_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-amber-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-amber-50 transition-colors"
                    data-testid="download-android-btn"
                  >
                    Android
                  </a>
                )}
                {appDownloadUrls.customer_appstore_url && (
                  <a
                    href={appDownloadUrls.customer_appstore_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-amber-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-amber-50 transition-colors"
                    data-testid="download-ios-btn"
                  >
                    iOS
                  </a>
                )}
                <button
                  onClick={() => {
                    setShowDownloadBanner(false);
                    localStorage.setItem("appBannerDismissed", "true");
                  }}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  data-testid="dismiss-download-banner"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          
          <div className="p-4 space-y-4">
            {/* Location Selection Card - FIRST */}
            <div 
              className="p-4 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border-2 border-primary/20 cursor-pointer hover:border-primary/50 transition-all"
              onClick={() => setShowMapPicker(true)}
              data-testid="open-map-picker"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-foreground">{t('where_to')}</h3>
                <div className="flex items-center gap-1 text-primary text-sm font-medium bg-primary/10 px-3 py-1 rounded-full">
                  <Map className="w-4 h-4" />
                  <span>{t('open_map')}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Location Permission Banner - shows when denied OR when we want to encourage enabling */}
                {locationPermissionDenied && (
                  <LocationPermissionBanner 
                    onRetry={() => {
                      setLocationPermissionDenied(false);
                      handleGetCurrentLocation();
                    }}
                    compact={true}
                  />
                )}
                
                {/* Use Current Location Button - Prominent */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // This will trigger the browser's permission popup if not yet asked
                    handleGetCurrentLocation();
                  }}
                  disabled={gettingCurrentLocation}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-semibold transition-all shadow-md disabled:opacity-50"
                  data-testid="use-current-location-btn"
                >
                  {gettingCurrentLocation ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('getting_location')}
                    </>
                  ) : (
                    <>
                      <Navigation className="w-4 h-4" />
                      {t('use_my_current_location')}
                    </>
                  )}
                </button>

                {/* Pickup Input - Prominent */}
                <div className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  pickup 
                    ? 'bg-green-50 dark:bg-green-950/30 border-green-400' 
                    : 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-300 border-dashed'
                }`}>
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-white text-sm font-bold">P</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-green-600 dark:text-green-400 font-semibold mb-0.5">{t('pickup')}</p>
                    <p className={`text-sm line-clamp-1 ${pickup ? 'text-foreground font-medium' : 'text-muted-foreground italic'}`}>
                      {pickup ? (pickup.shortAddress || pickup.address) : t('select_pickup_location')}
                    </p>
                  </div>
                  {pickup && <Check className="w-5 h-5 text-green-500 flex-shrink-0" />}
                </div>
                
                {/* Drop Input - Prominent */}
                <div className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  drop || tellDriverMode
                    ? 'bg-red-50 dark:bg-red-950/30 border-red-400' 
                    : 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-300 border-dashed'
                }`}>
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-white text-sm font-bold">D</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-red-600 dark:text-red-400 font-semibold mb-0.5">{t('drop_off')}</p>
                    <p className={`text-sm line-clamp-1 ${(drop || tellDriverMode) ? 'text-foreground font-medium' : 'text-muted-foreground italic'}`}>
                      {tellDriverMode 
                        ? `~${(approximateKm === "other" ? customKm : approximateKm) || '?'} km (${t('i_will_tell_the_driver')})`
                        : drop ? (drop.shortAddress || drop.address) : t('select_drop_location_on_map')}
                    </p>
                  </div>
                  {(drop || tellDriverMode) && <Check className="w-5 h-5 text-red-500 flex-shrink-0" />}
                </div>
                
                {(distance || (tellDriverMode && ((approximateKm && approximateKm !== "other") || customKm))) && (
                  <div className="text-center pt-3 mt-2 border-t-2 border-dashed border-primary/30">
                    <span className="text-sm text-muted-foreground">Distance: </span>
                    <span className="font-bold text-primary text-lg">
                      {tellDriverMode ? `~${(approximateKm === "other" ? customKm : approximateKm) || '?'}` : distance?.toFixed(1)} km
                    </span>
                    {tellDriverMode && <span className="text-xs text-muted-foreground ml-1">(approx)</span>}
                  </div>
                )}
              </div>
            </div>

            {/* I Will Tell Driver Option - Styled Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-blue-100 dark:border-blue-900 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{t('i_will_tell_the_driver')}</span>
                </div>
                <button
                  onClick={() => {
                    const newTellDriverMode = !tellDriverMode;
                    setTellDriverMode(newTellDriverMode);
                    if (newTellDriverMode) {
                      setApproximateKm(null);
                      setCustomKm("");
                    }
                  }}
                  className={`relative w-12 h-7 rounded-full transition-colors ${
                    tellDriverMode ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  data-testid="tell-driver-toggle"
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    tellDriverMode ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            
            {tellDriverMode && (
              <div className="space-y-3 mt-4 pt-4 border-t border-blue-100 dark:border-blue-900">
                <p className="text-xs text-muted-foreground">
                  {t('select_approximate_distance')}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {[2, 4, 6].map((km) => (
                    <button
                      key={km}
                      onClick={() => {
                        setApproximateKm(km);
                        setCustomKm("");
                      }}
                      className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                        approximateKm === km 
                          ? 'bg-blue-500 text-white border-blue-500 shadow-md' 
                          : 'bg-white dark:bg-gray-800 text-foreground border-gray-200 dark:border-gray-700 hover:border-blue-300'
                      }`}
                      data-testid={`km-option-${km}`}
                    >
                      {km} km
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setApproximateKm("other");
                      setCustomKm("");
                    }}
                    className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                      approximateKm === "other" || (!approximateKm && customKm)
                        ? 'bg-blue-500 text-white border-blue-500 shadow-md' 
                        : 'bg-white dark:bg-gray-800 text-foreground border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                    data-testid="km-option-other"
                  >
                    Other
                  </button>
                </div>
                {(approximateKm === "other" || customKm) && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      placeholder="Enter distance"
                      value={customKm}
                      onChange={(e) => {
                        setCustomKm(e.target.value);
                        if (approximateKm !== "other") setApproximateKm("other");
                      }}
                      autoFocus
                      className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="custom-km-input"
                    />
                    <span className="text-sm text-muted-foreground font-medium">km</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Saved Addresses - Styled Card */}
          {savedAddresses.length > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-amber-100 dark:border-amber-900 overflow-hidden">
              <button
                onClick={() => setSavedAddressesExpanded(!savedAddressesExpanded)}
                className="w-full p-4 flex items-center justify-between hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors"
                data-testid="toggle-saved-addresses"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                    <Bookmark className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{t('saved_locations')}</span>
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-medium">
                    {savedAddresses.length}
                  </span>
                </div>
                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${savedAddressesExpanded ? 'rotate-180' : ''}`} />
              </button>
              
              {savedAddressesExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-amber-100 dark:border-amber-900/50">
                  <div className="flex justify-end mb-3">
                    <button
                      onClick={() => setShowManageAddresses(true)}
                      className="text-xs text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-1"
                      data-testid="manage-addresses-btn"
                    >
                      <Edit3 className="w-3 h-3" />
                      {t('manage_all')}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {savedAddresses.slice(0, 4).map((addr) => {
                      const labelConfig = ADDRESS_LABELS.find(l => l.id === addr.label) || ADDRESS_LABELS[5];
                      const IconComponent = labelConfig.icon;
                      return (
                        <div 
                          key={addr.id} 
                          className="flex items-center justify-between p-3 bg-amber-50/50 dark:bg-amber-950/20 rounded-xl hover:bg-amber-100/50 dark:hover:bg-amber-950/30 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 border-2 border-amber-200 dark:border-amber-800 flex items-center justify-center flex-shrink-0">
                              <IconComponent className={`w-5 h-5 ${labelConfig.color}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold truncate">{addr.display_label}</p>
                              <p className="text-xs text-muted-foreground truncate">{addr.short_address || addr.address}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2.5 text-xs gap-1 border-green-400 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 font-semibold"
                              onClick={(e) => {
                                e.stopPropagation();
                                selectSavedAddress(addr, true);
                              }}
                              data-testid={`quick-pickup-${addr.id}`}
                            >
                              <Navigation className="w-3 h-3" />
                              {t('pickup')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2.5 text-xs gap-1 border-red-400 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 font-semibold"
                              onClick={(e) => {
                                e.stopPropagation();
                                selectSavedAddress(addr, false);
                              }}
                              data-testid={`quick-drop-${addr.id}`}
                            >
                              <MapPin className="w-3 h-3" />
                              {t('drop_off')}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {savedAddresses.length > 4 && (
                      <button
                        onClick={() => setShowManageAddresses(true)}
                        className="text-xs text-amber-600 hover:text-amber-700 font-semibold text-center py-2"
                      >
                        {t('view_all_saved_locations')} ({savedAddresses.length})
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Favorite Riders - Styled Card */}
          {favoriteRiders.length > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-pink-100 dark:border-pink-900 overflow-hidden">
              <button
                onClick={() => setFavoriteRidersExpanded(!favoriteRidersExpanded)}
                className="w-full p-4 flex items-center justify-between hover:bg-pink-50/50 dark:hover:bg-pink-950/20 transition-colors"
                data-testid="toggle-favorite-riders"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/50 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{t('favorite_riders')}</span>
                  <span className="text-xs bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-400 px-2 py-0.5 rounded-full font-medium">
                    {favoriteRiders.length}
                  </span>
                  {selectedFavoriteRider && (
                    <span className="text-xs bg-pink-500 text-white px-2 py-0.5 rounded-full font-semibold">
                      1 selected
                    </span>
                  )}
                </div>
                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${favoriteRidersExpanded ? 'rotate-180' : ''}`} />
              </button>
              
              {favoriteRidersExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-pink-100 dark:border-pink-900/50">
                  <div className="flex justify-end mb-3">
                    <button
                      onClick={() => setShowFavoriteRiders(true)}
                      className="text-xs text-pink-600 hover:text-pink-700 font-semibold flex items-center gap-1"
                      data-testid="manage-favorites-btn"
                    >
                      View All ({favoriteRiders.length})
                    </button>
                  </div>
                  <div className="space-y-2">
                    {favoriteRiders.slice(0, 3).map((fav) => {
                      const isSelected = selectedFavoriteRider?.id === fav.id;
                      const isOnline = fav.rider?.is_online;
                      const vehicleMismatch = selectedFavoriteRider && fav.rider?.vehicle_type && vehicleType && fav.rider.vehicle_type !== vehicleType;
                      return (
                        <button
                          key={fav.id}
                          onClick={() => {
                            if (fav.rider?.vehicle_type && vehicleType && fav.rider.vehicle_type !== vehicleType) {
                              toast.warning(
                                `This rider drives a ${fav.rider.vehicle_type}. Change vehicle type to ${fav.rider.vehicle_type} to select them.`,
                                { duration: 4000 }
                              );
                              return;
                            }
                            selectFavoriteRider(fav);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                            isSelected 
                              ? "bg-pink-100 dark:bg-pink-900/30 border-2 border-pink-500" 
                              : vehicleMismatch
                                ? "bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-300 dark:border-gray-600 opacity-60"
                                : "bg-pink-50/50 dark:bg-pink-950/20 hover:bg-pink-100/50 dark:hover:bg-pink-950/30 border-2 border-transparent"
                          }`}
                          data-testid={`favorite-rider-${fav.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 border flex items-center justify-center">
                                <User className="w-5 h-5 text-muted-foreground" />
                              </div>
                              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${isOnline ? "bg-green-500" : "bg-gray-400"}`} />
                            </div>
                            <div className="text-left">
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-sm">{fav.nickname || fav.rider?.name || fav.rider?.phone || "Rider"}</p>
                                {fav.rider?.vehicle_type && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                    fav.rider.vehicle_type === vehicleType 
                                      ? "bg-green-100 text-green-700" 
                                      : "bg-orange-100 text-orange-700"
                                  }`}>
                                    {fav.rider.vehicle_type}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {fav.rider?.rating && (
                                  <span className="flex items-center gap-0.5">
                                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                    {fav.rider.rating.toFixed(1)}
                                  </span>
                                )}
                                <span>•</span>
                                <span>{fav.ride_count || 0} rides</span>
                                <span>•</span>
                                <span className={isOnline ? "text-green-600" : "text-gray-500"}>
                                  {isOnline ? "Online" : "Offline"}
                                </span>
                              </div>
                            </div>
                          </div>
                          {isSelected ? (
                            <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">Selected</span>
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                      );
                    })}
                    {selectedFavoriteRider && (
                      <p className="text-xs text-muted-foreground text-center pt-1">
                        Will prioritize <span className="text-primary font-medium">{selectedFavoriteRider.nickname || selectedFavoriteRider.rider?.name}</span>. 
                        Tap again to deselect.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Vehicle Type Selection - Styled Card */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-100 dark:border-gray-800 p-4">
            <h3 className="font-bold text-lg text-foreground mb-4">{t('choose_vehicle')}</h3>
            <div className="grid grid-cols-3 gap-3">
              {vehicleSettings
                .filter(v => v.is_active)
                .map(vs => {
                  // Calculate if vehicle exceeds max ride/dropoff distance
                  const tripDistance = (pickup && drop) ? calculateDistance(pickup.lat, pickup.lng, drop.lat, drop.lng) : null;
                  const maxDropoff = vs.max_dropoff_distance || vs.max_ride_distance_km;
                  const exceedsMaxDropoff = tripDistance && maxDropoff && tripDistance > maxDropoff;
                  return { ...vs, exceedsMaxDropoff, tripDistance, maxDropoff };
                })
                .sort((a, b) => {
                  // Sort order: car, auto, bike
                  const order = { car: 0, auto: 1, bike: 2 };
                  return (order[a.vehicle_type] ?? 3) - (order[b.vehicle_type] ?? 3);
                })
                .map((vs) => {
                  // Vehicle is unavailable if no drivers OR exceeds max distance
                  const isUnavailable = (vs.is_available === false && pickup) || vs.exceedsMaxDropoff;
                  const unavailableReason = vs.exceedsMaxDropoff 
                    ? `Max ${Math.round(vs.maxDropoff)} km` 
                    : t('unavailable');
                  
                  return (
                <button
                  key={vs.vehicle_type}
                  onClick={() => {
                    if (isUnavailable) {
                      if (vs.exceedsMaxDropoff) {
                        toast.error(`${vs.display_name || vs.vehicle_type} not available for trips over ${Math.round(vs.maxDropoff)} km`);
                      } else {
                        // No drivers available - show vote modal
                        toast.error(`No ${vs.display_name || vs.vehicle_type} drivers available nearby`);
                        // Extract city from pickup address
                        if (pickup) {
                          const addressParts = (pickup.address || pickup.shortAddress || "").split(",");
                          const cityFromAddress = addressParts.length > 1 ? addressParts[addressParts.length - 2]?.trim() : addressParts[0]?.trim();
                          setVoteModalCity(cityFromAddress || "your area");
                          setShowCityVoteModal(true);
                        }
                      }
                      return;
                    }
                    
                    // Check if selected favorite rider has mismatched vehicle type
                    if (selectedFavoriteRider && selectedFavoriteRider.rider?.vehicle_type) {
                      const favoriteVehicleType = selectedFavoriteRider.rider.vehicle_type;
                      if (favoriteVehicleType !== vs.vehicle_type) {
                        toast.warning(
                          `${selectedFavoriteRider.nickname || selectedFavoriteRider.rider?.name || 'Your favorite rider'} drives a ${favoriteVehicleType}. Preference cleared.`,
                          { duration: 4000 }
                        );
                        setSelectedFavoriteRider(null);
                      }
                    }
                    
                    setVehicleType(vs.vehicle_type);
                    // Auto-select first active subtype
                    const firstActiveSubtype = vs.subtypes?.find(s => s.is_active);
                    setVehicleSubtype(firstActiveSubtype?.id || null);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all relative ${
                    vehicleType === vs.vehicle_type
                      ? "border-primary bg-primary/5"
                      : isUnavailable
                        ? "border-border opacity-50 cursor-not-allowed"
                        : "border-border hover:border-primary/30"
                  }`}
                  data-testid={`select-${vs.vehicle_type}-btn`}
                >
                  {isUnavailable && (
                    <span className="absolute top-1 right-1 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">
                      {unavailableReason}
                    </span>
                  )}
                  {!isUnavailable && vs.drivers_available > 0 && (
                    <span className="absolute top-1 right-1 text-[10px] bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full">
                      {vs.drivers_available} {t('nearby')}
                    </span>
                  )}
                  {vs.vehicle_type === "bike" ? (
                    <Bike className={`w-10 h-10 mx-auto ${vehicleType === vs.vehicle_type ? "text-primary" : "text-muted-foreground"}`} />
                  ) : vs.vehicle_type === "auto" ? (
                    <AutoRickshawIcon className={`w-10 h-10 mx-auto ${vehicleType === vs.vehicle_type ? "text-primary" : "text-muted-foreground"}`} />
                  ) : vs.vehicle_type?.startsWith("goods_carrier") ? (
                    <Truck className={`w-10 h-10 mx-auto ${vehicleType === vs.vehicle_type ? "text-primary" : "text-muted-foreground"}`} />
                  ) : (
                    <Car className={`w-10 h-10 mx-auto ${vehicleType === vs.vehicle_type ? "text-primary" : "text-muted-foreground"}`} />
                  )}
                  <p className="font-semibold mt-2">
                    {vs.display_name || 
                     (vs.vehicle_type?.startsWith("goods_carrier") ? "Goods Carrier" : 
                      (vs.vehicle_type === "bike" || vs.vehicle_type === "auto" || vs.vehicle_type === "car" ? t(vs.vehicle_type) : 
                       vs.vehicle_type?.charAt(0).toUpperCase() + vs.vehicle_type?.slice(1).replace(/_/g, ' ')))}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {vs.vehicle_type === "bike" ? t('quick_cheap') : 
                     vs.vehicle_type === "auto" ? t('comfortable') : 
                     vs.vehicle_type?.startsWith("goods_carrier") ? "Delivery" : 
                     vs.vehicle_type === "car" ? t('premium') : ""}
                  </p>
                </button>
              );
              })}
            </div>
            
            {/* Subtype Selection - Dynamic based on vehicle settings */}
            {(() => {
              const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
              const activeSubtypes = currentVehicle?.subtypes?.filter(s => s.is_active) || [];
              
              if (activeSubtypes.length > 1) {
                return (
                  <div className="mt-3">
                    <p className="text-sm text-muted-foreground mb-2">Select {currentVehicle.display_name || vehicleType} Type</p>
                    <div className="grid grid-cols-3 gap-2">
                      {activeSubtypes.map((subtype) => (
                        <button
                          key={subtype.id}
                          onClick={() => setVehicleSubtype(subtype.id)}
                          className={`p-2 rounded-lg border-2 text-sm transition-all ${
                            vehicleSubtype === subtype.id
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/30"
                          }`}
                          data-testid={`select-${subtype.id}-btn`}
                        >
                          <p className="font-semibold">{subtype.name}</p>
                          <p className="text-xs text-muted-foreground">{subtype.description}</p>
                          {(subtype.multiplier || subtype.fare_multiplier) !== 1.0 && (subtype.multiplier || subtype.fare_multiplier) !== undefined && (
                            <p className={`text-xs mt-1 ${(subtype.multiplier || subtype.fare_multiplier) < 1 ? 'text-green-600' : 'text-blue-600'}`}>
                              {(subtype.multiplier || subtype.fare_multiplier) < 1 
                                ? `${Math.round((1 - (subtype.multiplier || subtype.fare_multiplier)) * 100)}% off` 
                                : `${(subtype.multiplier || subtype.fare_multiplier)}x`}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                    
                    {/* Allow Subtype Matching Option */}
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="allow-subtype-matching"
                        checked={allowSubtypeMatching}
                        onChange={(e) => setAllowSubtypeMatching(e.target.checked)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                        data-testid="allow-subtype-matching-checkbox"
                      />
                      <label htmlFor="allow-subtype-matching" className="text-sm text-muted-foreground">
                        {t('allow_other_subtypes') || 'Match with other available subtypes if my choice is unavailable'}
                      </label>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Booking Type Selection - Styled Card */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-100 dark:border-gray-800 p-4">
            <h3 className="font-bold text-lg text-foreground mb-4">{t('booking_type')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {(() => {
                // Get current vehicle settings to check enabled booking types
                const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
                const allBookingTypes = [
                  { id: "one_way", labelKey: "one_way", icon: "→", descKey: "point_a_to_b", enabledKey: "one_way_enabled" },
                  { id: "two_way", labelKey: "round_trip", icon: "↔", descKey: "a_to_b_back", enabledKey: "two_way_enabled" },
                  { id: "hourly", labelKey: "hourly", icon: "⏱", descKey: "pay_per_hour", enabledKey: "hourly_enabled" },
                  { id: "daily", labelKey: "daily", icon: "📅", descKey: "full_day_rental", enabledKey: "daily_enabled" },
                  { id: "package", labelKey: "package_delivery", icon: "📦", descKey: "send_parcel", enabledKey: "package_enabled" },
                ];
                
                // Filter to only show enabled booking types
                const enabledTypes = allBookingTypes.filter(type => {
                  // If vehicle settings don't exist or flag not set, default to enabled
                  if (!currentVehicle) return true;
                  const isEnabled = currentVehicle[type.enabledKey];
                  return isEnabled !== false; // Show if true or undefined
                });
                
                // Reset booking type if current selection is disabled
                if (enabledTypes.length > 0 && !enabledTypes.find(t => t.id === bookingType)) {
                  setBookingType(enabledTypes[0].id);
                }
                
                return enabledTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setBookingType(type.id)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      bookingType === type.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/30"
                    }`}
                    data-testid={`booking-type-${type.id}-btn`}
                  >
                    <span className="text-xl">{type.icon}</span>
                    <p className="font-semibold text-sm mt-1">{t(type.labelKey)}</p>
                    <p className="text-xs text-muted-foreground">{t(type.descKey)}</p>
                  </button>
                ));
              })()}
            </div>

            {/* Booking Type Specific Options */}
            {bookingType === "two_way" && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Estimated Wait Time</span>
                  <span className="text-sm text-muted-foreground">{estimatedWaitMinutes} mins</span>
                </div>
                <Input
                  type="range"
                  min="15"
                  max="180"
                  step="15"
                  value={estimatedWaitMinutes}
                  onChange={(e) => setEstimatedWaitMinutes(parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>15 min</span>
                  <span>3 hours</span>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Waiting charges: ₹{bookingPricing?.two_way?.waiting_charge_per_minute || 2}/min
                </p>
              </div>
            )}

            {bookingType === "hourly" && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{t('number_of_hours')}</span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setHours(Math.max(bookingPricing?.hourly?.min_hours || 1, hours - 1))}
                      disabled={hours <= (bookingPricing?.hourly?.min_hours || 1)}
                    >
                      -
                    </Button>
                    <span className="w-8 text-center font-bold">{hours}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setHours(Math.min(bookingPricing?.hourly?.max_hours || 12, hours + 1))}
                      disabled={hours >= (bookingPricing?.hourly?.max_hours || 12)}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  ₹{bookingPricing?.hourly?.rate || 100}{t('per_hour')} • {t('includes')} {bookingPricing?.hourly?.kms_cap_per_hour || 10}{t('km')}{t('per_hour')}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {t('extra_km')}: ₹{bookingPricing?.hourly?.extra_km_charge || 12}/{t('km')}
                </p>
                <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {t('drop_optional')}
                </p>
              </div>
            )}

            {bookingType === "daily" && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{t('number_of_days')}</span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDays(Math.max(bookingPricing?.daily?.min_days || 1, days - 1))}
                      disabled={days <= (bookingPricing?.daily?.min_days || 1)}
                    >
                      -
                    </Button>
                    <span className="w-8 text-center font-bold">{days}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDays(Math.min(bookingPricing?.daily?.max_days || 30, days + 1))}
                      disabled={days >= (bookingPricing?.daily?.max_days || 30)}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  ₹{bookingPricing?.daily?.rate || 1500}{t('per_day')} • {t('includes')} {bookingPricing?.daily?.max_kms_per_day || 150}{t('km')}{t('per_day')}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {t('extra_km')}: ₹{bookingPricing?.daily?.extra_km_charge || 10}/{t('km')}
                </p>
                <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {t('drop_optional')}
                </p>
              </div>
            )}

            {bookingType === "package" && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('package_weight_kg')}</span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPackageWeight(Math.max(0.5, packageWeight - 1))}
                      disabled={packageWeight <= 0.5}
                    >
                      -
                    </Button>
                    <input
                      type="number"
                      min="0.5"
                      max="5000"
                      step="0.5"
                      value={packageWeight}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val) && val >= 0.5 && val <= 5000) {
                          setPackageWeight(val);
                        } else if (e.target.value === '') {
                          setPackageWeight(0.5);
                        }
                      }}
                      className="w-20 text-center font-bold bg-background border border-border rounded-md px-2 py-1 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="text-sm text-muted-foreground">kg</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPackageWeight(Math.min(5000, packageWeight + 1))}
                      disabled={packageWeight >= 5000}
                    >
                      +
                    </Button>
                  </div>
                </div>
                
                {/* Receiver Phone Number - Required */}
                <div className="pt-2 border-t border-border">
                  <label className="text-sm font-medium flex items-center gap-1 mb-1.5">
                    <Phone className="w-4 h-4" />
                    Receiver Phone Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    placeholder="Enter receiver's phone number"
                    value={receiverPhone}
                    onChange={(e) => setReceiverPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                    className="w-full"
                    data-testid="receiver-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Driver will contact receiver for package delivery
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  ₹{bookingPricing?.package?.base_fare || 30} base + ₹{bookingPricing?.package?.per_km_rate || 8}/km
                </p>
                <p className="text-xs text-muted-foreground">
                  Max weight included: {bookingPricing?.package?.max_weight_kg || 10}kg
                </p>
                {packageWeight > (bookingPricing?.package?.max_weight_kg || 10) && (
                  <p className="text-xs text-amber-600">
                    Extra weight charge: ₹{bookingPricing?.package?.extra_weight_charge || 5}/kg over {bookingPricing?.package?.max_weight_kg || 10}kg
                  </p>
                )}
              </div>
            )}
          </div>

          {/* More Services Section - Only show if enabled by SuperAdmin */}
          {moreServicesConfig.section_enabled && moreServicesConfig.services?.length > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-100 dark:border-gray-800 p-4">
              <h3 className="font-bold text-lg text-foreground mb-4 flex items-center gap-2">
                <span className="text-lg">✨</span>
                {moreServicesConfig.section_title || t('more_services')}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {moreServicesConfig.services.map((service) => (
                  <button 
                    key={service.id}
                    onClick={() => {
                      if (service.url) {
                        window.open(service.url, '_blank');
                      } else {
                        setShowComingSoon(true);
                      }
                    }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 hover:shadow-md transition-all cursor-pointer group"
                    style={{ 
                      backgroundColor: `${service.color_from}10`,
                      borderColor: `${service.color_from}40`
                    }}
                    data-testid={`service-${service.id}-link`}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform text-xl"
                      style={{ 
                        background: `linear-gradient(135deg, ${service.color_from}, ${service.color_to})` 
                      }}
                    >
                      {service.icon}
                    </div>
                    <div className="text-center">
                      <p 
                        className="font-semibold text-sm text-foreground transition-colors"
                        style={{ '--hover-color': service.color_from }}
                      >
                        {service.name}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {service.description || (service.url ? 'Tap to open' : 'Coming Soon')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Coming Soon Dialog */}
          <Dialog open={showComingSoon} onOpenChange={setShowComingSoon}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                    <span className="text-white text-lg">✨</span>
                  </div>
                  Farmster
                </DialogTitle>
              </DialogHeader>
              <div className="text-center py-4">
                <p className="text-lg font-semibold text-foreground mb-2">Coming Soon!</p>
                <p className="text-sm text-muted-foreground">
                  Farm equipment rentals will be available soon. Stay tuned for updates!
                </p>
              </div>
              <Button onClick={() => setShowComingSoon(false)} className="w-full">
                Got it
              </Button>
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Sticky Footer - Book Ride Button */}
        {(() => {
          const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
          const tripDistance = (pickup && drop) ? calculateDistance(pickup.lat, pickup.lng, drop.lat, drop.lng) : null;
          const maxDropoff = currentVehicle?.max_dropoff_distance || currentVehicle?.max_ride_distance_km;
          const exceedsMaxDistance = tripDistance && maxDropoff && tripDistance > maxDropoff;
          const isVehicleUnavailable = pickup && (currentVehicle?.is_available === false || exceedsMaxDistance);
          const isDropOptional = bookingType === "hourly" || bookingType === "daily" || tellDriverMode;
          
          // For tell driver mode, check if approximate km is set
          const hasTellDriverKm = tellDriverMode && ((approximateKm && approximateKm !== "other") || (customKm && parseFloat(customKm) > 0));
          const isDisabled = !pickup || !vehicleType || (!isDropOptional && !drop) || (tellDriverMode && !hasTellDriverKm) || isVehicleUnavailable;
          
          return (
            <div className="fixed left-0 right-0 bg-white dark:bg-gray-900 border-t-2 border-gray-200 dark:border-gray-800 p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-40 bottom-[72px] md:bottom-0">
              {isVehicleUnavailable && vehicleType && (
                <div className="flex items-center gap-2 p-3 mb-3 bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 rounded-xl text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {exceedsMaxDistance 
                      ? `${currentVehicle?.display_name || vehicleType} not available for trips over ${Math.round(maxDropoff)} km.`
                      : `No ${currentVehicle?.display_name || vehicleType} drivers available nearby.`
                    }
                    {' '}Please select a different vehicle type.
                  </span>
                </div>
              )}
              <Button
                className="w-full h-14 rounded-2xl font-bold text-lg shadow-lg"
                onClick={estimateFare}
                disabled={isDisabled}
                data-testid="get-fare-btn"
              >
                {(() => {
                  if (!pickup) return t('select_pickup');
                  if (!vehicleType) return 'Select Vehicle';
                  if (tellDriverMode && !hasTellDriverKm) return 'Select Distance';
                  if (!drop && !isDropOptional) return t('select_drop');
                  if (isVehicleUnavailable) return 'Vehicle Unavailable';
                  return t('get_fare_estimate');
                })()}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          );
        })()}
      </div>
      )}

      {/* Fare Estimate Drawer */}
      <Drawer open={showBookingSheet} onOpenChange={setShowBookingSheet}>
        <DrawerContent data-testid="fare-drawer">
          <DrawerHeader>
            <DrawerTitle>{t('confirm_your_ride')}</DrawerTitle>
          </DrawerHeader>
          {fareEstimate && (
            <div className="px-4 pb-4">
              <div className="p-4 bg-muted/50 rounded-xl mb-4 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5" />
                  <p className="text-sm">{pickup?.shortAddress || pickup?.address}</p>
                </div>
                {drop && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5" />
                    <p className="text-sm">{drop?.shortAddress || drop?.address}</p>
                  </div>
                )}
                {tellDriverMode && !drop && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5" />
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      ~{(approximateKm === "other" ? customKm : approximateKm)} km (You'll tell the driver)
                    </p>
                  </div>
                )}
                {!drop && (bookingType === "hourly" || bookingType === "daily") && !tellDriverMode && (
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5" />
                    <p className="text-sm text-muted-foreground italic">{t('drop_to_be_decided')}</p>
                  </div>
                )}
              </div>

              {/* Surge Pricing Alert */}
              {fareEstimate.surge_multiplier > 1 && (
                <div className="p-3 mb-4 bg-orange-100 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-orange-600 font-bold">{fareEstimate.surge_multiplier}x</span>
                    <span className="text-sm text-orange-700 dark:text-orange-400">{t('surge_pricing')}</span>
                  </div>
                  {fareEstimate.surge_reason && (
                    <p className="text-xs text-orange-600/80 mt-1">{fareEstimate.surge_reason}</p>
                  )}
                </div>
              )}

              <div className="space-y-3 mb-4">
                <div className="fare-row">
                  <span className="text-muted-foreground">{t('booking_type')}</span>
                  <span className="font-medium capitalize">
                    {t(bookingType === "one_way" ? "one_way" : 
                       bookingType === "two_way" ? "round_trip" :
                       bookingType === "hourly" ? "hourly" : 
                       bookingType === "daily" ? "daily" : "package_delivery")}
                  </span>
                </div>
                <div className="fare-row">
                  <span className="text-muted-foreground">Vehicle</span>
                  <span className="font-medium capitalize">
                    {vehicleType}
                    {vehicleSubtype && (() => {
                      const currentVehicle = vehicleSettings.find(v => v.vehicle_type === vehicleType);
                      const selectedSubtype = currentVehicle?.subtypes?.find(s => s.id === vehicleSubtype);
                      return selectedSubtype ? (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({selectedSubtype.name})
                        </span>
                      ) : null;
                    })()}
                  </span>
                </div>
                <div className="fare-row">
                  <span className="text-muted-foreground">Distance</span>
                  <span className="font-medium font-numbers">
                    {bookingType === "two_way" ? `${fareEstimate.round_trip_distance || fareEstimate.distance_km * 2} km (round trip)` : `${fareEstimate.distance_km} km`}
                  </span>
                </div>
                
                {/* Booking type specific details */}
                {bookingType === "hourly" && (
                  <>
                    <div className="fare-row">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-medium font-numbers">{fareEstimate.hours || hours} hours</span>
                    </div>
                    <div className="fare-row">
                      <span className="text-muted-foreground">KMs Included</span>
                      <span className="font-medium font-numbers">{fareEstimate.kms_included || 0} km</span>
                    </div>
                    {fareEstimate.extra_kms > 0 && (
                      <div className="fare-row text-amber-600">
                        <span>Extra KM Charge</span>
                        <span className="font-medium font-numbers">+₹{fareEstimate.extra_km_charge}</span>
                      </div>
                    )}
                  </>
                )}
                
                {bookingType === "daily" && (
                  <>
                    <div className="fare-row">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-medium font-numbers">{fareEstimate.days || days} days</span>
                    </div>
                    <div className="fare-row">
                      <span className="text-muted-foreground">KMs Included</span>
                      <span className="font-medium font-numbers">{fareEstimate.kms_included || 0} km</span>
                    </div>
                    {fareEstimate.extra_kms > 0 && (
                      <div className="fare-row text-amber-600">
                        <span>Extra KM Charge</span>
                        <span className="font-medium font-numbers">+₹{fareEstimate.extra_km_charge}</span>
                      </div>
                    )}
                  </>
                )}
                
                {bookingType === "two_way" && (
                  <>
                    <div className="fare-row">
                      <span className="text-muted-foreground">Est. Wait Time</span>
                      <span className="font-medium font-numbers">{fareEstimate.estimated_wait_minutes || estimatedWaitMinutes} mins</span>
                    </div>
                    {fareEstimate.waiting_charge > 0 && (
                      <div className="fare-row text-amber-600">
                        <span>Waiting Charge</span>
                        <span className="font-medium font-numbers">+₹{fareEstimate.waiting_charge}</span>
                      </div>
                    )}
                  </>
                )}
                
                {bookingType === "one_way" && (
                  <div className="fare-row">
                    <span className="text-muted-foreground">Est. Time</span>
                    <span className="font-medium font-numbers">{fareEstimate.estimated_time_mins} mins</span>
                  </div>
                )}
                
                {bookingType === "package" && (
                  <>
                    <div className="fare-row">
                      <span className="text-muted-foreground">Package Weight</span>
                      <span className="font-medium font-numbers">{fareEstimate.package_weight_kg || packageWeight} kg</span>
                    </div>
                    <div className="fare-row">
                      <span className="text-muted-foreground">Max Included</span>
                      <span className="font-medium font-numbers">{fareEstimate.max_weight_kg || 10} kg</span>
                    </div>
                    {fareEstimate.extra_weight_kg > 0 && (
                      <div className="fare-row text-amber-600">
                        <span>Extra Weight Charge ({fareEstimate.extra_weight_kg}kg)</span>
                        <span className="font-medium font-numbers">+₹{fareEstimate.extra_weight_charge}</span>
                      </div>
                    )}
                    <div className="fare-row">
                      <span className="text-muted-foreground">Est. Delivery Time</span>
                      <span className="font-medium font-numbers">{fareEstimate.estimated_time_mins} mins</span>
                    </div>
                  </>
                )}
                
                {fareEstimate.surge_multiplier > 1 && (
                  <div className="fare-row">
                    <span className="text-muted-foreground">Base Fare</span>
                    <span className="font-medium font-numbers">₹{fareEstimate.base_fare}</span>
                  </div>
                )}
                {promoApplied && (
                  <div className="fare-row text-green-600">
                    <span>Discount</span>
                    <span className="font-medium font-numbers">-₹{promoApplied.discount_amount}</span>
                  </div>
                )}
                <div className="fare-total">
                  <span>Estimated Total</span>
                  <span className="text-primary font-numbers text-2xl">
                    ₹{promoApplied ? promoApplied.final_amount : fareEstimate.fare}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-1">
                  Final amount may vary based on actual distance and time
                </p>
              </div>

              {/* Promo Code Input */}
              <div className="mb-4 p-3 bg-muted/30 rounded-xl border border-border">
                <p className="text-sm font-medium mb-2">Have a promo code?</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="flex-1 uppercase"
                    disabled={promoApplied}
                    data-testid="promo-code-input"
                  />
                  {promoApplied ? (
                    <Button
                      variant="outline"
                      onClick={() => { setPromoApplied(null); setPromoCode(""); }}
                      data-testid="remove-promo-btn"
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={applyPromoCode}
                      disabled={applyingPromo || !promoCode}
                      data-testid="apply-promo-btn"
                    >
                      {applyingPromo ? "..." : "Apply"}
                    </Button>
                  )}
                </div>
                {promoApplied && (
                  <p className="text-xs text-green-600 mt-2">Promo applied! You save ₹{promoApplied.discount_amount}</p>
                )}
              </div>

              {/* Tip Selection */}
              <div className="mb-4 p-3 bg-muted/30 rounded-xl border border-border">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Add a tip for your rider</p>
                  {(tipAmount > 0 || (showCustomTip && customTip)) && (
                    <button
                      onClick={() => { setTipAmount(0); setShowCustomTip(false); setCustomTip(""); }}
                      className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                      data-testid="clear-tip-btn"
                    >
                      Clear tip
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mb-2">
                  {[0, 10, 20, 30].map((tip) => (
                    <button
                      key={tip}
                      onClick={() => { 
                        setTipAmount(tip); 
                        setShowCustomTip(false); 
                        setCustomTip(""); 
                      }}
                      className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        tipAmount === tip && !showCustomTip
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/30"
                      }`}
                      data-testid={`tip-${tip}-btn`}
                    >
                      {tip === 0 ? "No Tip" : `₹${tip}`}
                    </button>
                  ))}
                  <button
                    onClick={() => { setShowCustomTip(true); setTipAmount(0); }}
                    className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      showCustomTip
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/30"
                    }`}
                    data-testid="tip-custom-btn"
                  >
                    Other
                  </button>
                </div>
                {showCustomTip && (
                  <Input
                    type="number"
                    placeholder="Enter tip amount"
                    value={customTip}
                    onChange={(e) => setCustomTip(e.target.value)}
                    className="w-full"
                    min="0"
                    data-testid="custom-tip-input"
                  />
                )}
                {(tipAmount > 0 || (showCustomTip && customTip)) && (
                  <p className="text-xs text-green-600 mt-2">
                    Tip: ₹{showCustomTip ? (customTip || 0) : tipAmount} will be added
                  </p>
                )}
              </div>
            </div>
          )}
          <DrawerFooter>
            <Button
              className="w-full h-14 rounded-full font-semibold text-lg"
              onClick={bookRide}
              disabled={loading}
              data-testid="confirm-booking-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <IndianRupee className="w-5 h-5 mr-1" />
                  Book for Est. ₹{(promoApplied ? promoApplied.final_amount : fareEstimate?.fare) + (showCustomTip ? (parseFloat(customTip) || 0) : tipAmount)}
                  {(tipAmount > 0 || (showCustomTip && customTip)) && (
                    <span className="text-xs ml-1 opacity-80">(incl. tip)</span>
                  )}
                </>
              )}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Rating Dialog - Using Extracted Component */}
      <RatingDialog
        open={showRating}
        onOpenChange={setShowRating}
        rating={rating}
        setRating={setRating}
        review={review}
        setReview={setReview}
        onSubmit={submitRating}
        submitting={ratingSubmitting}
        driverName={activeRide?.driver_name}
        isRiderFavorite={isRiderFavorite}
        onAddToFavorites={activeRide?.driver_id ? () => addToFavorites(activeRide.driver_id) : null}
        addingToFavorites={addingToFavorites}
      />

      {/* Cancellation Dialog - Using Extracted Component */}
      <CancelRideDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        customCancelReason={customCancelReason}
        setCustomCancelReason={setCustomCancelReason}
        reassignToOther={reassignToOther}
        setReassignToOther={setReassignToOther}
        cancelling={cancelling}
        onCancel={(shouldReassign) => cancelRide(shouldReassign)}
        hasDriver={!!activeRide?.driver_id}
      />

      {/* Chat Modal - Coming Soon */}
      {showChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold">Chat Coming Soon!</h3>
            <p className="text-muted-foreground">
              We're working on bringing you an in-app chat feature to communicate with your driver. Stay tuned!
            </p>
            <Button onClick={() => setShowChat(false)} className="w-full">
              Got it
            </Button>
          </div>
        </div>
      )}

      {/* Call functionality removed - use phone call instead */}

      {/* Save Address Dialog - Shows after booking */}
      <Dialog open={showSaveAddressDialog} onOpenChange={setShowSaveAddressDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-primary" />
              Save Address for Quick Booking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Save these locations for faster booking next time!
            </p>
            
            {/* Pickup Address Save Option */}
            {addressToSave.pickup && (
              <SaveAddressItem
                address={addressToSave.pickup}
                type="Pickup"
                labels={ADDRESS_LABELS}
                onSave={async (label, customLabel) => {
                  const success = await saveAddress(addressToSave.pickup, label, customLabel);
                  if (success) {
                    setAddressToSave(prev => ({ ...prev, pickup: null }));
                    if (!addressToSave.dropoff) setShowSaveAddressDialog(false);
                  }
                }}
                saving={savingAddress}
              />
            )}
            
            {/* Dropoff Address Save Option */}
            {addressToSave.dropoff && (
              <SaveAddressItem
                address={addressToSave.dropoff}
                type="Drop-off"
                labels={ADDRESS_LABELS}
                onSave={async (label, customLabel) => {
                  const success = await saveAddress(addressToSave.dropoff, label, customLabel);
                  if (success) {
                    setAddressToSave(prev => ({ ...prev, dropoff: null }));
                    if (!addressToSave.pickup) setShowSaveAddressDialog(false);
                  }
                }}
                saving={savingAddress}
              />
            )}
            
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowSaveAddressDialog(false)}
            >
              Skip for now
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Saved Addresses Dialog */}
      <Dialog open={showManageAddresses} onOpenChange={setShowManageAddresses}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-primary" />
              Saved Addresses ({savedAddresses.length}/10)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {savedAddresses.length === 0 ? (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No saved addresses yet</p>
                <p className="text-sm text-muted-foreground">Save locations after booking for quick access</p>
              </div>
            ) : (
              savedAddresses.map((addr) => {
                const labelConfig = ADDRESS_LABELS.find(l => l.id === addr.label) || ADDRESS_LABELS[5];
                const IconComponent = labelConfig.icon;
                return (
                  <div
                    key={addr.id}
                    className="p-3 bg-muted/30 rounded-lg border border-border/50 space-y-2"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center`}>
                          <IconComponent className={`w-4 h-4 ${labelConfig.color}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{addr.display_label}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{addr.address}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => selectSavedAddress(addr, true)}
                          title="Set as Pickup"
                        >
                          <Navigation className="w-3.5 h-3.5 text-green-500" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => selectSavedAddress(addr, false)}
                          title="Set as Drop-off"
                        >
                          <MapPin className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => deleteAddress(addr.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            
            {savedAddresses.length < 10 && (
              <p className="text-xs text-center text-muted-foreground pt-2">
                You can save up to 10 addresses. Book a ride to save new locations!
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Favorite Riders Dialog */}
      <Dialog open={showFavoriteRiders} onOpenChange={setShowFavoriteRiders}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              </div>
              <div>
                <span className="block">Favorite Riders</span>
                <span className="text-xs font-normal text-muted-foreground">{favoriteRiders.length} of 10 slots used</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {favoriteRiders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-red-300" />
                </div>
                <p className="font-medium text-foreground">No favorite riders yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Complete a ride and give 4+ stars<br/>to add a rider to favorites
                </p>
              </div>
            ) : (
              favoriteRiders.map((fav) => {
                const isOnline = fav.rider?.is_online;
                const isSelected = selectedFavoriteRider?.id === fav.id;
                return (
                  <div
                    key={fav.id}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? "bg-red-50 dark:bg-red-900/20 border-red-400" 
                        : "bg-card border-border hover:border-red-200 dark:hover:border-red-800"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar with online status */}
                      <div className="relative flex-shrink-0">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/50 dark:to-red-800/50 flex items-center justify-center border-2 border-white dark:border-gray-700 shadow-sm">
                          <User className="w-7 h-7 text-red-600 dark:text-red-400" />
                        </div>
                        <span className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800 ${isOnline ? "bg-green-500" : "bg-gray-400"}`} />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-foreground truncate">
                            {fav.nickname || fav.rider?.name || fav.rider?.phone || "Rider"}
                          </p>
                          {isSelected && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] rounded-full font-medium">
                              SELECTED
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3 mt-1">
                          {fav.rider?.rating && (
                            <span className="flex items-center gap-1 text-sm">
                              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                              <span className="font-medium">{fav.rider.rating.toFixed(1)}</span>
                            </span>
                          )}
                          <span className="text-sm text-muted-foreground">
                            {fav.ride_count || 0} rides together
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                            isOnline 
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" 
                              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-green-500" : "bg-gray-400"}`} />
                            {isOnline ? "Online now" : "Offline"}
                          </span>
                          {fav.rider?.vehicle_type && (
                            <span className="text-xs text-muted-foreground">
                              {fav.rider.vehicle_type.charAt(0).toUpperCase() + fav.rider.vehicle_type.slice(1)}
                              {fav.rider.vehicle_number && ` • ${fav.rider.vehicle_number}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/50">
                      <Button
                        size="sm"
                        variant={isSelected ? "default" : "outline"}
                        className={`flex-1 ${isSelected ? "bg-red-500 hover:bg-red-600" : "border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"}`}
                        onClick={() => {
                          selectFavoriteRider(fav);
                          setShowFavoriteRiders(false);
                        }}
                      >
                        {isSelected ? "Deselect" : "Request this rider"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => removeFromFavorites(fav.id, fav.rider_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {favoriteRiders.length > 0 && favoriteRiders.length < 10 && (
            <div className="pt-3 border-t text-center">
              <p className="text-xs text-muted-foreground">
                {10 - favoriteRiders.length} slots remaining • Rate riders 4+ stars to add
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* City Vote Modal - shown when no drivers available */}
      <CityVoteModal
        isOpen={showCityVoteModal}
        onClose={() => setShowCityVoteModal(false)}
        pickupLocation={pickup}
        cityName={voteModalCity}
        customer={user}
      />
    </div>
  );
};

// Helper component for saving individual address
const SaveAddressItem = ({ address, type, labels, onSave, saving }) => {
  const [selectedLabel, setSelectedLabel] = useState("");
  const [customLabel, setCustomLabel] = useState("");

  return (
    <div className="p-3 bg-muted/30 rounded-lg border border-border/50 space-y-3">
      <div className="flex items-start gap-2">
        <div className={`w-6 h-6 rounded-full ${type === "Pickup" ? "bg-green-500" : "bg-red-500"} flex items-center justify-center flex-shrink-0`}>
          <span className="text-white text-[10px] font-bold">{type[0]}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{type} Location</p>
          <p className="text-sm line-clamp-2">{address?.address || address?.shortAddress}</p>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {labels.map((label) => (
          <button
            key={label.id}
            onClick={() => setSelectedLabel(label.id)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs transition-all ${
              selectedLabel === label.id
                ? "bg-primary text-primary-foreground"
                : "bg-muted hover:bg-muted/80"
            }`}
          >
            <label.icon className="w-3 h-3" />
            {label.id}
          </button>
        ))}
      </div>
      
      {selectedLabel === "Other" && (
        <Input
          placeholder="Enter custom label (e.g., Gym, Hospital)"
          value={customLabel}
          onChange={(e) => setCustomLabel(e.target.value)}
          className="h-9 text-sm"
          maxLength={20}
        />
      )}
      
      <Button
        size="sm"
        className="w-full"
        disabled={!selectedLabel || (selectedLabel === "Other" && !customLabel.trim()) || saving}
        onClick={() => onSave(selectedLabel, customLabel.trim() || null)}
      >
        {saving ? (
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
        ) : (
          <Plus className="w-4 h-4 mr-2" />
        )}
        Save as {selectedLabel === "Other" && customLabel ? customLabel : selectedLabel || "..."}
      </Button>
    </div>
  );
};

const HistoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRides = async () => {
      try {
        const res = await axios.get(`${API}/rides/customer/${user.id}`);
        setRides(res.data);
      } catch (err) {
        toast.error("Failed to load ride history");
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchRides();
  }, [user]);

  const handleRebook = (ride) => {
    // Store rebook data in localStorage for BookingPage to read
    const rebookData = {
      pickup: ride.pickup,
      dropoff: ride.dropoff || ride.drop,
      vehicleType: ride.vehicle_type,
      vehicleSubtype: ride.vehicle_subtype,
      bookingType: ride.booking_type || "one_way",
      bookingDetails: ride.booking_details || {},
    };
    localStorage.setItem("rebookData", JSON.stringify(rebookData));
    toast.success("Locations pre-filled! Customize and book.");
    navigate("/customer");
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="customer-history-page">
      <div className="p-4">
        <h2 className="text-xl font-bold text-foreground mb-1">Ride History</h2>
        <p className="text-sm text-muted-foreground mb-4">{rides.length} rides completed</p>
      </div>

      <div className="px-4 pb-4 space-y-3">
        {rides.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border border-border">
            <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No rides yet</p>
            <p className="text-sm text-muted-foreground mt-1">Your ride history will appear here</p>
          </div>
        ) : (
          rides.map((ride) => (
            <div
              key={ride.id}
              className="p-4 bg-card rounded-xl border border-border/50"
              data-testid={`ride-${ride.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {ride.vehicle_type === "bike" ? (
                    <Bike className="w-5 h-5 text-primary" />
                  ) : ride.vehicle_type === "auto" ? (
                    <AutoRickshawIcon className="w-5 h-5 text-accent" />
                  ) : (
                    <Car className="w-5 h-5 text-accent" />
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium status-${ride.status}`}>
                    {ride.status.replace("_", " ")}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-numbers font-bold text-foreground">
                    ₹{(ride.fare || 0) + (ride.tip_amount || 0)}
                  </span>
                  {ride.tip_amount > 0 && (
                    <p className="text-xs text-green-600">+₹{ride.tip_amount} tip</p>
                  )}
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                  <span className="text-muted-foreground line-clamp-1">{ride.pickup?.address}</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                  <span className="text-muted-foreground line-clamp-1">{(ride.dropoff || ride.drop)?.address}</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                <span className="text-xs text-muted-foreground">
                  {new Date(ride.created_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <div className="flex items-center gap-2">
                  {ride.rating && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-medium">{ride.rating}</span>
                    </div>
                  )}
                  {/* Re-book button - only show for completed/cancelled rides with valid locations */}
                  {["completed", "cancelled"].includes(ride.status) && ride.pickup && (ride.dropoff || ride.drop) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 text-primary border-primary/30 hover:bg-primary/10"
                      onClick={() => handleRebook(ride)}
                      data-testid={`rebook-${ride.id}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      Re-book
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const ProfilePage = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [referralInfo, setReferralInfo] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Name editing state
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    const fetchReferralInfo = async () => {
      try {
        const res = await axios.get(`${API}/referral/my-code?user_id=${user.id}`);
        setReferralInfo(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    if (user?.id) fetchReferralInfo();
  }, [user?.id]);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.post(`${API}/auth/account/delete`, {
        user_id: user.id,
        confirmation: 'DELETE'
      });
      setShowDeleteModal(false);
      toast.success('Your account has been permanently deleted');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingName(true);
    try {
      await axios.put(`${API}/users/${user.id}/profile`, { name: newName.trim() });
      updateUser({ ...user, name: newName.trim() });
      setEditingName(false);
      toast.success('Name updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  return (
    <div data-testid="customer-profile-page">
      <div className="p-6 bg-gradient-to-br from-primary/20 to-primary/5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <User className="w-8 h-8 text-primary" />
            )}
          </div>
          <div className="flex-1">
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="h-8 text-lg font-bold"
                  placeholder="Enter your name"
                  data-testid="edit-name-input"
                />
                <Button size="sm" onClick={handleSaveName} disabled={savingName}>
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditingName(false); setNewName(user?.name || ''); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-foreground">{user?.name}</h1>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => { setNewName(user?.name || ''); setEditingName(true); }}
                  className="h-6 w-6 p-0"
                  data-testid="edit-name-btn"
                >
                  <Pencil className="w-3 h-3" />
                </Button>
              </div>
            )}
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Wallet Balance */}
        {referralInfo && referralInfo.wallet_balance > 0 && (
          <div className="p-4 bg-gradient-to-r from-green-500/10 to-green-600/5 rounded-xl border border-green-500/30">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Wallet Balance</span>
              <span className="text-2xl font-bold text-green-500">₹{referralInfo.wallet_balance}</span>
            </div>
          </div>
        )}

        {/* Referral Code */}
        <div className="p-4 bg-card rounded-xl border border-border/50">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Refer & Earn</h3>
          <div className="p-3 bg-muted/50 rounded-lg text-center mb-2">
            <span className="font-mono text-xl font-bold text-primary">
              {referralInfo?.referral_code || user?.referral_code || "Loading..."}
            </span>
          </div>
          <p className="text-xs text-muted-foreground text-center">Share this code with friends and both earn rewards!</p>
          {referralInfo?.successful_referrals > 0 && (
            <p className="text-xs text-green-500 text-center mt-2">
              {referralInfo.successful_referrals} successful referrals
            </p>
          )}
        </div>

        <div className="p-4 bg-card rounded-xl border border-border/50">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Account Details</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-muted-foreground" />
                <span>Phone</span>
              </div>
              <span className="text-muted-foreground">{user?.phone || "Not added"}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-amber-400" />
                <span>Total Rides</span>
              </div>
              <span className="font-medium">{user?.total_rides || 0}</span>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <NotificationSettings userId={user?.id} userType="customer" />

        <Button
          variant="outline"
          className="w-full h-12 border-destructive text-destructive hover:bg-destructive/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Logout
        </Button>

        {/* Delete Account - Subtle placement */}
        <button
          onClick={() => setShowDeleteModal(true)}
          className="w-full text-center text-sm text-muted-foreground hover:text-destructive transition-colors py-2"
          data-testid="delete-account-btn"
        >
          Delete Account
        </button>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
                <Trash2 className="w-8 h-8 text-destructive" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Delete Account</h3>
            </div>
            
            <p className="text-sm text-muted-foreground text-center">
              This action is permanent and cannot be undone. All your data including ride history and saved addresses will be permanently deleted.
            </p>
            
            <div>
              <label className="text-sm text-foreground block mb-2">
                Type <span className="font-bold">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE"
                className="w-full px-4 py-3 border border-border rounded-xl text-center text-lg"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
              >
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
